import ComplianceBoard from './components/ComplianceBoard';
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Link } from 'react-router-dom';
import './App.css';
import Onboarding from './components/Onboarding';
import Settings from './components/Settings';
import TenantDetail from './components/TenantDetail';
import PendingMessages from './components/PendingMessages';
import Section8 from './components/Section8';
import Section8Case from './components/Section8Case';
import ComplianceUpload from './components/ComplianceUpload';
// Item 1 frontend (2026-02) — pending compliance contractor quotes.
import BulkUpload from './components/BulkUpload';
import { ClassifyDocModal } from './components/ClassifyDocModal';
import { MaintenanceKanban, MaintenanceDetail } from './components/Maintenance';
import AdminDashboard from './components/AdminDashboard';
import AdminConcierge from './components/AdminConcierge';
import LeadDetail from './components/LeadDetail';
import AvatarMenu from './components/AvatarMenu';
import SupportTicketModal from './components/SupportTicketModal';
import BusinessEmailSlideOver from './components/BusinessEmailSlideOver';
import { usePagination, SeeMoreButton, DashboardSeeMore } from './components/usePagination';
import { ArchiveButton } from './components/ArchiveControls';
import { LeadsFullPage, ArrearsFullPage, ActiveCasesFullPage, MaintenanceFullPage } from './components/DashboardSectionPages';
import estatelLogo from './assets/estatel-logo.png';
import Sidebar from './components/Sidebar';

// 2026-02 (iter 14, Issue 1b) — Error boundary specifically for the
// realtime alerts panel. If ANY single alert row (or the panel body
// itself) throws during render, the boundary catches it and shows a
// small "one or more alerts failed to render — retry" pill in place
// of the panel body, so the panel container itself NEVER vanishes.
// The rest of the dashboard keeps working. Without this, a single
// malformed alert doc (missing title, null body, unexpected field
// shape) could unmount the whole React subtree.
class AlertsPanelErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error(
      '[AlertsPanel] render crashed — component tree kept alive by '
      + 'boundary. Investigate the alert doc shape:',
      error, info,
    );
  }
  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };
  render() {
    if (this.state.hasError) {
      return (
        <div
          className="realtime-alerts-list"
          data-testid="realtime-alerts-boundary-fallback"
          style={{
            padding: '0.75rem 1rem',
            background: '#fff7ed',
            borderLeft: '4px solid #ea580c',
            color: '#7c2d12',
            fontSize: '0.875rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            justifyContent: 'space-between',
          }}
        >
          <span>One or more alerts failed to render. The rest of the
            dashboard is still working.</span>
          <button
            type="button"
            onClick={this.handleRetry}
            data-testid="realtime-alerts-boundary-retry"
            style={{
              background: '#ea580c', color: '#fff', border: 'none',
              padding: '0.25rem 0.75rem', borderRadius: 4,
              fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
import PropertiesList from './components/PropertiesList';
import PropertyDetail from './components/PropertyDetail';
import Contractors from './components/Contractors';
import ContractorReplyTriage from './components/ContractorReplyTriage';
import { formatCurrency } from './utils/currency';
import { formatDate, formatDateTime } from './utils/dates';
import { decodeText } from './utils/maintenanceText';
import { AuthProvider } from './auth/AuthContext';
import { ProtectedRoute } from './auth/ProtectedRoute';
import LoginPage from './auth/LoginPage';
import SignupPage from './auth/SignupPage';
import ForgotPasswordPage from './auth/ForgotPasswordPage';
import ResetPasswordPage from './auth/ResetPasswordPage';
import VerifyEmailPage from './auth/VerifyEmailPage';
import SetupChecklistBanner from './auth/SetupChecklistBanner';
import PropertiesWithoutLandlordBanner from './components/PropertiesWithoutLandlordBanner';
import TrialBanner from './components/TrialBanner';
import DemoDataBanner from './components/DemoDataBanner';
import ConciergeBanner from './components/ConciergeBanner';
import AICommandChat from './components/AICommandChat';
import { useSetupAllClear } from './auth/useSetupAllClear';
import { useAuth } from './auth/AuthContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API_KEY = ''; // Step 41 — auth via httpOnly cookie; this stub keeps existing 'Bearer ' headers harmless

// 2026-02 — WhatsApp soft-disable feature flag. When false (default) the
// entire WhatsApp UI is hidden from the dashboard, sidebar and Settings.
// Underlying backend routes, models and integrations remain intact for
// one-line reactivation via `REACT_APP_WHATSAPP_ENABLED=true`.
const WHATSAPP_ENABLED = String(process.env.REACT_APP_WHATSAPP_ENABLED || 'false').toLowerCase() === 'true';

// Step 46 — Shared empty-state panel for the main dashboard sections.
// One consistent look across Tenants, Landlords, Arrears, Active S8,
// Duplicates, Unknown WhatsApp, Available Properties, Leads and Maintenance.
function EmptyStatePanel({ title, description, actions, testid }) {
  return (
    <div
      data-testid={testid || 'empty-state-panel'}
      style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 10,
        padding: '2.25rem 1.75rem',
        textAlign: 'center',
        color: '#475569',
      }}
    >
      {title && (
        <p style={{ margin: 0, fontSize: '1.02rem', fontWeight: 600, color: '#0f172a' }}>
          {title}
        </p>
      )}
      {description && (
        <p style={{ margin: '0.5rem auto 0', maxWidth: 640, fontSize: '0.92rem', lineHeight: 1.55, color: '#475569' }}>
          {description}
        </p>
      )}
      {actions && actions.length > 0 && (
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginTop: '1.25rem', flexWrap: 'wrap' }}>
          {actions.map((a, i) => (
            <button
              key={i}
              type="button"
              onClick={a.onClick}
              data-testid={a.testid}
              style={{
                background: a.variant === 'secondary' ? '#e2e8f0' : '#0073EA',
                color: a.variant === 'secondary' ? '#1e293b' : '#fff',
                border: 'none',
                padding: '0.6rem 1.1rem',
                borderRadius: 6,
                fontSize: '0.92rem',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


const STAGE_LABELS = {
  1: 'Arrears Detected',
  2: 'Threshold Crossed',
  3: 'Notice Preparation',
  4: 'Notice Served',
  5: 'Notice Period Active',
  6: 'Start Court Proceedings',
  7: 'Hearing Preparation',
  8: 'Outcome Recorded'
};

const FLAG_REASON_MAP = {
  'E1': 'Message contains a legal keyword',
  'E2': 'Tenant has an active Section 8 case',
  'E3': 'Tenant arrears exceed the 2-month threshold',
  'E4': 'A previous message with this tenant was flagged for review',
  'E5': 'First ever message to this tenant — manual review required',
  'E6': 'Tenant is requesting to speak to a person',
  'E7': 'You are in Draft and Approve mode — all messages require your approval',
  'E8': 'Active Section 8 case — all communications held for review',
  'OVERRIDE 1': 'Section 8 case at Stage 3 or above — draft-and-approve enforced',
  'OVERRIDE 2': 'First ever contact with this tenant — draft-and-approve enforced',
  'OVERRIDE 3': 'An escalation rule was triggered — message held for your review',
};

const humaniseFlagReason = (raw) => {
  if (!raw) return '';
  return raw.split('; ').map(part => {
    const code = Object.keys(FLAG_REASON_MAP).find(k => part.startsWith(k + ':') || part.startsWith(k + ' '));
    return code ? FLAG_REASON_MAP[code] : part;
  }).join('; ');
};

const ALERT_TYPE_LABELS = {
  'unknown_whatsapp': 'Unknown WhatsApp Number',
  's8_threshold': 'Section 8 Threshold Crossed',
  'pending_message': 'New Pending Message',
  'compliance_expired': 'Compliance Document Expired',
  'compliance_unclassified_doc': 'Document Needs Classification',
  'licence_expiring': 'Property Licence Expiring Soon',
  'licence_expired': 'Property Licence Expired',
  'court_deadline_48h': 'Court Filing Deadline Within 48 Hours',
  'hearing_48h': 'Hearing Date Within 48 Hours',
  'pending_4h': 'Pending Message Unactioned',
  'pending_24h': 'Pending Message Critical — Over 24 Hours',
  'new_unknown_email': 'New Email Enquiry — Unknown Sender',
  'new_unknown_whatsapp': 'New WhatsApp Enquiry — Unknown Number',
  'tenant_email_inbound': 'Tenant Replied by Email',
  // 2026-06-05 — Contractor outreach alerts.
  'contractor_reply': 'Contractor Quote Received',
  'maintenance_contractor_escalation': 'Maintenance Escalated to Specialists',
  'maintenance_contractor_no_response': 'Handyman Has Not Responded',
  'maintenance_no_contractor': 'No Contractor Available for Maintenance',
  'maintenance_new': 'New Maintenance Request',
  'maintenance_emergency': 'Emergency Maintenance',
  // 2026-02 (Imp 6) — Follow-up promised by AI; needs agent reply.
  'tenant_followup_required': 'Follow-up Promised — Agent Reply Needed',
  // 2026-02 (Imp 7) — Agent Decision Required (abandoned belongings,
  // ending tenancy, complaints).
  'agent_decision_required': 'Agent Decision Required',
  // 2026-02 — Business / administrative email (skipped lead triage).
  'business_email_received': 'Business Email Received',
  // 2026-02 (iter 6) — v2 maintenance flow alerts.
  'maintenance_new_v2': 'New Maintenance Request',
  'maintenance_emergency_v2': 'Emergency Maintenance',
  'maintenance_no_contractor_v2': 'No Contractor on File',
  'maintenance_no_availability_match_v2': 'Maintenance — Availability Mismatch',
  'maintenance_tenant_availability_requested_v2': 'Tenant Availability Requested',
  'maintenance_pending_tenant_selection_v2': 'Tenant Picking Slot',
  'maintenance_tenant_declined_v2': 'Tenant Declined Booking',
  'maintenance_contractor_reply_unparsed_v2': 'Contractor Reply Needs Review',
  'maintenance_contractor_late_reply_v2': 'Contractor Late Reply (Already Assigned)',
  'maintenance_contractor_declined_v2': 'Contractor Declined Job',
  'maintenance_visit_confirmed_v2': 'Maintenance Visit Confirmed',
  'maintenance_quote_received_v2': 'Contractor Quote Received',
  'maintenance_reply_to_closed_ref_v2': 'Tenant Replied on Closed Request',
  'maintenance_reply_to_unknown_ref_v2': 'Tenant Cited Unknown Reference',
  'maintenance_ambiguous_tenant_reply_v2': 'Ambiguous Tenant Reply — Human Decision Needed',
  'maintenance_stuck_awaiting_v2': 'Maintenance — Stuck Awaiting Tenant',
  // 2026-02 (iter 13) — new v2 alert types.
  // BUG 1 — actionable tenant note attached to an existing job. Click
  // navigates to the maintenance card so the agent can review.
  'maintenance_tenant_note_v2': 'Tenant Note on Existing Job — Review',
  // BUG 1 safety-net — router returned an unexpected outcome. Should
  // never fire under current code; if it does, code-path defect.
  'maintenance_router_fell_through_v2': 'Maintenance Router Fell Through — Investigate',
  // BUG 5 — an outbound MR-referencing send was blocked at pre-send
  // re-validation. Surfaces any future ghost-send attempt.
  'maintenance_ghost_send_prevented_v2': 'Ghost Send Prevented — Investigate Source Path',
  // Iter 22 P0 — Re-negotiation flow (tenant asked to reschedule
  // after booking, contractor replied). Rebooked = happy path.
  'maintenance_reneg_contractor_reply_v2': 'Reschedule — Contractor Reply Needs Review',
  'maintenance_reneg_contractor_counter_v2': 'Reschedule — Contractor Counter Forwarded',
  'maintenance_reneg_rebooked_v2': 'Reschedule Accepted — Rebooked',
  // Iter 22 Item 3 — Scoping-gate non-maintenance verdicts.
  'tenant_lettings_enquiry_v2':   'Lettings Enquiry — Tenant Asked About Available Property',
  'tenant_viewing_request_v2':    'Viewing Request — Tenant Asked to View',
  'tenant_general_enquiry_v2':    'General Enquiry — Non-Maintenance',
  'maintenance_uncertain_review_v2': 'Uncertain — Human Review Needed',
  // Attach-path report-vs-question split.
  'maintenance_tenant_question_v2': 'Tenant Question on Open Job — Review + Answer',
};

const ALERT_LEVEL_STYLES = {
  critical: { bg: '#fef2f2', border: '#dc2626', color: '#991b1b', label: 'CRITICAL', badgeBg: '#FEE2E2', badgeText: '#DC2626' },
  urgent:   { bg: '#fffbeb', border: '#d97706', color: '#92400e', label: 'URGENT',   badgeBg: '#FEF3C7', badgeText: '#D97706' },
  warning:  { bg: '#fefce8', border: '#ca8a04', color: '#713f12', label: 'WARNING',  badgeBg: '#FEF9C3', badgeText: '#CA8A04' },
  info:     { bg: '#eff6ff', border: '#2563eb', color: '#1e40af', label: 'INFO',     badgeBg: '#DBEAFE', badgeText: '#2563EB' },
};

function App() {
  const { logout: authLogout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // View state
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard', 'onboarding', 'settings', 'tenants', 'landlords'
  const [pendingScroll, setPendingScroll] = useState(null);
  // Step 45 — deep-link state: when the setup checklist Set-up button is
  // clicked we pre-select the right Settings tab and jump into settings.
  const [settingsInitialTab, setSettingsInitialTab] = useState(null);

  // 2026-05-06 (Item 2 — companion) — cross-route deep-link via URL
  // search params. External pages (e.g. /section8/...) navigate to
  //   /?view=settings&tab=agency_details
  // which renders the dashboard route (`/*`) and we read the params on
  // mount to flip currentView + settingsInitialTab. Once consumed the
  // params are cleared from the address bar so a manual refresh stays
  // on the dashboard.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const view = params.get('view');
    const tab = params.get('tab');
    if (view === 'settings') {
      if (tab) setSettingsInitialTab(tab);
      setCurrentView('settings');
      // Strip the consumed query string so refresh ≠ jump-to-settings.
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, '', cleanUrl);
    } else if (view === 'properties') {
      // 2026-05-06 (Item 3 — companion) — cross-route nav from
      // TenantDetail's "Go to Properties" fallback when the tenant has
      // no linked property.
      setCurrentView('properties');
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, '', cleanUrl);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Step 45 — toggles the sidebar "Onboarding" entry. null = loading, we
  // simply hide the entry in that case; false hides after completion.
  const setupAllClear = useSetupAllClear(currentView);
  
  // Dashboard data
  const [pendingMessages, setPendingMessages] = useState([]);
  const [activeCases, setActiveCases] = useState([]);
  const [arrearsOverview, setArrearsOverview] = useState([]);
  const [complianceData, setComplianceData] = useState([]);
  const [complianceBoardKey, setComplianceBoardKey] = useState(0);
  const [tenancies, setTenancies] = useState([]);
  const [digestSummary, setDigestSummary] = useState(null);
  const [whatsappAlerts, setWhatsappAlerts] = useState([]);
  const [duplicateAlerts, setDuplicateAlerts] = useState([]);
  const [allTenants, setAllTenants] = useState([]);
  const [s8Alerts, setS8Alerts] = useState([]);
  const [availableProperties, setAvailableProperties] = useState([]);
  // 2026-06-04 — Contractors count for the orange dashboard tile.
  const [contractorCount, setContractorCount] = useState(0);
  // Step 45 — Add-Tenant modal state for the manual tenant creation flow.
  const [showAddTenantModal, setShowAddTenantModal] = useState(false);
  // Step 47 — Add-Lead modal state for the manual lead creation flow.
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  // Step 53 BUG 3 — Add-Property modal.
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const [leads, setLeads] = useState([]);
  const [leadCounts, setLeadCounts] = useState({ new: 0, total: 0 });
  const [maintenanceCounts, setMaintenanceCounts] = useState({ open_count: 0, total: 0 });
  // 2026-05-29 — Compliance Scanner sidebar badge count.
  const [scannerIssueCount, setScannerIssueCount] = useState(0);
  
  // Real-time notification alerts
  const [realtimeAlerts, setRealtimeAlerts] = useState([]);
  // 2026-02 (IMP 11) — Support ticket modal open/close state. Driven
  // by the Avatar menu's "Support" item, available on every page.
  const [supportOpen, setSupportOpen] = useState(false);
  // 2026-02 — Business email slide-over.
  const [bizEmailAlert, setBizEmailAlert] = useState(null);
  const [classifyAlert, setClassifyAlert] = useState(null);
  const [bizFlash, setBizFlash] = useState('');

  // 2026-02 FIX — Dashboard sections cap at 6 items; clicking "See more"
  // navigates to a dedicated full page (see /leads-all, /arrears-all,
  // /section8-cases-all, /messages/pending). Full-list pages keep the
  // 25-per-page pagination.
  const pendingPager = usePagination(pendingMessages, 6);
  const visiblePendingMessages = pendingPager.visible;
  const activeCasesPager = usePagination(activeCases, 6);
  const visibleActiveCases = activeCasesPager.visible;
  const leadsPager = usePagination(leads, 6);
  const visibleLeads = leadsPager.visible;
  const arrearsPager = usePagination(digestSummary?.arrears_details || [], 6);
  const visibleArrears = arrearsPager.visible;
  // 2026-02 FIX 1 — All Tenants (Tenants view) and tenancyList pagination.
  const tenantsPager = usePagination(allTenants || [], 25);
  const visibleTenants = tenantsPager.visible;
  // 2026-02 (IMP 6) — Follow-up wording suggestion modal state.
  const [followupModal, setFollowupModal] = useState(null);
  // shape: { alertId, tenantId, tenantName, tenantEmail, subject, bodyText, loading, sending, error }
  const [alertsPanelOpen, setAlertsPanelOpen] = useState(true);
  
  // Duplicate resolution state
  const [resolvingAlert, setResolvingAlert] = useState(null);
  const [resolveDetails, setResolveDetails] = useState(null);
  const [resolveLoading, setResolveLoading] = useState(false);
  
  // UI state
  const [tenanciesExpanded, setTenanciesExpanded] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [showComplianceUpload, setShowComplianceUpload] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  
  // Maintenance state
  const [maintDetailId, setMaintDetailId] = useState(null);
  const [maintHighlightMrId, setMaintHighlightMrId] = useState(null);
  // 2026-06-05 — Contractor reply triage modal. Carries the
  // ContractorOutreach id (NOT a maintenance request id) so the panel
  // can load the specific contractor's thread.
  const [contractorTriageOutreachId, setContractorTriageOutreachId] = useState(null);
  const [tenancyList, setTenancyList] = useState([]);
  const [kanbanRefreshKey, setKanbanRefreshKey] = useState(0);

  // Expand state for clickable cards
  const [expandedWaAlert, setExpandedWaAlert] = useState(null);
  const [expandedDupAlert, setExpandedDupAlert] = useState(null);

  // Billing/subscription state
  const [billingInfo, setBillingInfo] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // Tenant search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimerRef = React.useRef(null);
  const searchRef = React.useRef(null);
  
  // Fetch all dashboard data
  useEffect(() => {
    fetchDashboardData();
  }, []);
  
  // Fetch real-time alerts + poll every 60s
  const fetchRealtimeAlerts = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/notifications/alerts/active`, {
        headers: { 'Authorization': `Bearer ${API_KEY}` },
      });
      if (res.ok) {
        const data = await res.json();
        const raw = data.alerts || [];
        // 2026-02 — Strip WhatsApp-related alert types from the UI when
        // the feature flag is off. Backend may still produce them; this
        // is purely cosmetic so the agent does not see references to a
        // channel that is currently disabled.
        const filtered = WHATSAPP_ENABLED
          ? raw
          : raw.filter(a => !['unknown_whatsapp', 'new_unknown_whatsapp'].includes(a.alert_type));
        setRealtimeAlerts(filtered);
      }
    } catch (err) {
      console.error('Error fetching realtime alerts:', err);
    }
  };
  
  useEffect(() => {
    fetchRealtimeAlerts();
    // FIX 6 (2026-05-23): poll faster (was 60s) so repeat emails surface
    // without a hard refresh. Backend dedups + bubbles to top via
    // `refresh_existing`, so we just need the UI to pull more often.
    //
    // 2026-02 (iter 22 hotfix C) — Sidebar Maintenance counter (and
    // every other sidebar badge fed by `fetchDashboardData`) was
    // stale between agent clicks because the interval only pulled
    // realtime alerts. Pull the dashboard summary in the same tick
    // so escalations/pending actions surface in the sidebar
    // immediately, not only after the operator interacts.
    const interval = setInterval(() => {
      fetchRealtimeAlerts();
      fetchDashboardData(true);
    }, 15000);
    // Cross-component trigger: any page (TenantDetail, PendingMessages,
    // MaintenanceDetail, etc.) can dispatch this event after an action that
    // triggers backend alert auto-dismiss, so the panel refreshes instantly
    // instead of waiting for the 60s poll.
    //
    // 2026-02 (Item 6) — also re-pull the dashboard summary so the
    // sidebar badge counts (maintenance, compliance, arrears, leads,
    // section 8) clear the moment an item is resolved/archived/dismissed.
    const onExternalRefresh = () => {
      fetchRealtimeAlerts();
      fetchDashboardData(true);
    };
    window.addEventListener('eiq-alerts-refresh', onExternalRefresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener('eiq-alerts-refresh', onExternalRefresh);
    };
  }, []);
  
  const dismissRealtimeAlert = async (alertId) => {
    try {
      await fetch(`${API_BASE_URL}/api/notifications/alerts/${alertId}/dismiss`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${API_KEY}` },
      });
      setRealtimeAlerts(prev => prev.filter(a => a._id !== alertId));
    } catch (err) {
      console.error('Error dismissing alert:', err);
    }
  };

  // 2026-02 (IMP 6) — Open the Suggest Follow-up modal, call backend
  // for an AI-drafted reply, allow inline edit, send, auto-dismiss.
  const openFollowupSuggest = async (alert) => {
    const tenantId = alert.reference_id;
    setFollowupModal({
      alertId: alert._id,
      tenantId,
      tenantName: '',
      tenantEmail: '',
      subject: '',
      bodyText: '',
      loading: true,
      sending: false,
      error: '',
    });
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/tenant-followup/suggest?tenant_id=${encodeURIComponent(tenantId)}`,
        { credentials: 'include', headers: { 'Authorization': `Bearer ${API_KEY}` } },
      );
      if (!res.ok) throw new Error('Suggest failed');
      const data = await res.json();
      setFollowupModal(prev => prev && prev.alertId === alert._id ? {
        ...prev,
        loading: false,
        tenantName: data.tenant_name || '',
        tenantEmail: data.tenant_email || '',
        subject: data.subject || '',
        bodyText: data.body_text || '',
      } : prev);
    } catch (e) {
      setFollowupModal(prev => prev && prev.alertId === alert._id ? {
        ...prev, loading: false, error: 'Could not draft a follow-up. Please try again.',
      } : prev);
    }
  };

  const sendFollowupNow = async () => {
    if (!followupModal) return;
    const fm = followupModal;
    if (!fm.bodyText.trim()) {
      setFollowupModal(prev => prev ? { ...prev, error: 'Body is required' } : prev);
      return;
    }
    setFollowupModal(prev => prev ? { ...prev, sending: true, error: '' } : prev);
    try {
      const res = await fetch(`${API_BASE_URL}/api/tenant-followup/send`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${API_KEY}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: fm.tenantId,
          alert_id: fm.alertId,
          subject: fm.subject,
          body_text: fm.bodyText,
        }),
      });
      if (!res.ok) throw new Error('Send failed');
      setRealtimeAlerts(prev => prev.filter(a => a._id !== fm.alertId));
      setFollowupModal(null);
    } catch (e) {
      setFollowupModal(prev => prev ? { ...prev, sending: false,
        error: 'Send failed. Please try again.' } : prev);
    }
  };
  
  const fetchDashboardData = async (isBackground = false) => {
    try {
      if (!isBackground) setLoading(true);
      setError(null);
      
      const headers = {
        'Authorization': `Bearer ${API_KEY}`,
        'Content-Type': 'application/json'
      };
      
      // Fetch all data in parallel
      const [pendingRes, digestRes, alertsRes, dupAlertsRes, s8AlertsRes, availPropsRes, leadsRes, leadCountsRes, maintSummaryRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/pending-messages`, { headers }),
        fetch(`${API_BASE_URL}/api/digest-summary`, { headers }),
        fetch(`${API_BASE_URL}/api/whatsapp/alerts`, { headers }),
        fetch(`${API_BASE_URL}/api/alerts/duplicates`, { headers }),
        fetch(`${API_BASE_URL}/api/section8/alerts`, { headers }),
        fetch(`${API_BASE_URL}/api/properties/available`, { headers }),
        fetch(`${API_BASE_URL}/api/leads`, { headers }),
        fetch(`${API_BASE_URL}/api/leads/summary/counts`, { headers }),
        fetch(`${API_BASE_URL}/api/maintenance/summary`, { headers })
      ]);
      
      if (!pendingRes.ok || !digestRes.ok) {
        throw new Error('Failed to fetch dashboard data');
      }
      
      const pending = await pendingRes.json();
      const digest = await digestRes.json();
      const alerts = alertsRes.ok ? await alertsRes.json() : [];
      const dupAlerts = dupAlertsRes.ok ? await dupAlertsRes.json() : [];
      const s8AlertsList = s8AlertsRes.ok ? await s8AlertsRes.json() : [];
      let availProps = [];
      if (availPropsRes.ok) {
        const text = await availPropsRes.text();
        availProps = text ? JSON.parse(text) : [];
      }
      let leadsList = [];
      if (leadsRes.ok) {
        const text = await leadsRes.text();
        leadsList = text ? JSON.parse(text) : [];
      }
      let leadCountsData = { new: 0, total: 0 };
      if (leadCountsRes.ok) {
        const text = await leadCountsRes.text();
        leadCountsData = text ? JSON.parse(text) : { new: 0, total: 0 };
      }
      let maintSummary = { open_count: 0, total: 0 };
      if (maintSummaryRes.ok) {
        const text = await maintSummaryRes.text();
        maintSummary = text ? JSON.parse(text) : { open_count: 0, total: 0 };
      }
      
      setPendingMessages(pending);
      setDigestSummary(digest);
      setActiveCases(digest.active_cases || []);
      setWhatsappAlerts(alerts);
      setDuplicateAlerts(dupAlerts);
      setS8Alerts(s8AlertsList);
      setAvailableProperties(availProps);
      setLeads(leadsList);
      setLeadCounts(leadCountsData);
      setMaintenanceCounts(maintSummary);
      // 2026-06-04 — Lightweight contractor count for the orange tile.
      try {
        const cRes = await fetch(`${API_BASE_URL}/api/contractors`, { credentials: 'include' });
        if (cRes.ok) {
          const cData = await cRes.json();
          setContractorCount(cData.total ?? (cData.contractors?.length || 0));
        }
      } catch (_e) { /* silent */ }

      // Fetch tenancy list for maintenance form
      try {
        const tRes = await fetch(`${API_BASE_URL}/api/maintenance/tenancies`, { headers });
        if (tRes.ok) setTenancyList(await tRes.json());
      } catch {}

      // Fetch billing info
      try {
        const bRes = await fetch(`${API_BASE_URL}/api/stripe/billing`, { headers });
        if (bRes.ok) {
          const bText = await bRes.text();
          setBillingInfo(bText ? JSON.parse(bText) : null);
        }
      } catch {}

      // 2026-05-29 — Compliance Scanner sidebar badge count.
      try {
        const csRes = await fetch(`${API_BASE_URL}/api/compliance/scanner/summary`,
                                  { credentials: 'include' });
        if (csRes.ok) {
          const cs = await csRes.json();
          setScannerIssueCount((cs.red || 0) + (cs.amber || 0));
        }
      } catch {}
      
      if (!isBackground) setLoading(false);

      // Keep the realtime-alert panel in sync with backend auto-dismiss
      // whenever the dashboard refreshes (approve/reject, compliance upload,
      // link tenant, kanban updates, etc. all call this).
      fetchRealtimeAlerts();
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message);
      if (!isBackground) setLoading(false);
    }
  };
  
  // Tenant search handler with debounce
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (value.length < 2) { setSearchResults([]); setSearchOpen(false); return; }
    setSearchOpen(true);
    setSearchLoading(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/tenants/search?q=${encodeURIComponent(value)}`, {
          headers: { 'Authorization': `Bearer ${API_KEY}` },
        });
        if (res.ok) {
          const data = await res.json();
          setSearchResults(data.results || []);
        }
      } catch {} finally { setSearchLoading(false); }
    }, 250);
  };

  // Close search dropdown on outside click
  React.useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) { setSearchOpen(false); }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchAllTenants = async () => {
    try {
      const headers = {
        'Authorization': `Bearer ${API_KEY}`,
        'Content-Type': 'application/json'
      };
      const res = await fetch(`${API_BASE_URL}/api/tenants/list/all`, { headers });
      if (res.ok) {
        setAllTenants(await res.json());
      }
    } catch (err) {
      console.error('Error fetching tenants:', err);
    }
  };
  
  const handleApprove = async (msg, edited = false) => {
    // 2026-02 (Bug 2 refix v5) — Accept the full msg (was
    // messageId) so the maintenance-forward source-type
    // detection works on the dashboard section too.
    try {
      const messageId = typeof msg === 'string' ? msg : msg._id;
      const src = (typeof msg === 'object' && msg) ? msg.source_type : null;
      const headers = {
        'Authorization': `Bearer ${API_KEY}`,
        'Content-Type': 'application/json'
      };

      if (src === 'maintenance_forward') {
        const base = `${API_BASE_URL}/api/maintenance/${msg.maintenance_request_id}/pending-messages/${messageId}`;
        if (edited && editedContent) {
          const eRes = await fetch(`${base}/edit`, {
            method: 'POST', headers,
            body: JSON.stringify({ draft_body: editedContent }),
          });
          if (!eRes.ok) throw new Error('Failed to save edited draft');
        }
        const aRes = await fetch(`${base}/approve`, {
          method: 'POST', headers, body: JSON.stringify({}),
        });
        if (!aRes.ok) throw new Error('Failed to approve maintenance forward');
        await fetchDashboardData();
        setEditingMessage(null);
        setEditedContent('');
        return;
      }

      const body = {
        approved_by: 'Agent',  // In production, get from auth context
        ...(edited && editedContent ? { edited_content_text: editedContent } : {})
      };
      
      const response = await fetch(
        `${API_BASE_URL}/api/pending-message/${messageId}/approve`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(body)
        }
      );
      
      if (!response.ok) throw new Error('Failed to approve message');
      
      // Refresh data
      await fetchDashboardData();
      setEditingMessage(null);
      setEditedContent('');
    } catch (err) {
      alert('Error approving message: ' + err.message);
    }
  };
  
  const handleReject = async (msg) => {
    try {
      const messageId = typeof msg === 'string' ? msg : msg._id;
      const src = (typeof msg === 'object' && msg) ? msg.source_type : null;
      const headers = {
        'Authorization': `Bearer ${API_KEY}`,
        'Content-Type': 'application/json'
      };

      const url = src === 'maintenance_forward'
        ? `${API_BASE_URL}/api/maintenance/${msg.maintenance_request_id}/pending-messages/${messageId}/dismiss`
        : `${API_BASE_URL}/api/pending-message/${messageId}/reject`;

      const response = await fetch(url, { method: 'POST', headers });
      
      if (!response.ok) throw new Error('Failed to reject message');
      
      // Refresh data
      await fetchDashboardData();
    } catch (err) {
      alert('Error rejecting message: ' + err.message);
    }
  };
  
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Flash highlight so user notices the target section
      element.classList.add('section-highlight');
      setTimeout(() => element.classList.remove('section-highlight'), 2000);
    }
  };

  // Navigate from any view back to the dashboard and scroll to a section
  const navigateToSection = (sectionId) => {
    if (currentView !== 'dashboard') {
      setPendingScroll(sectionId);
      setCurrentView('dashboard');
    } else {
      scrollToSection(sectionId);
    }
  };

  // Consume any pending scroll request once the dashboard finishes rendering
  useEffect(() => {
    if (currentView === 'dashboard' && pendingScroll) {
      const id = pendingScroll;
      setPendingScroll(null);
      // Wait one paint for layout, then scroll
      setTimeout(() => scrollToSection(id), 150);
    }
  }, [currentView, pendingScroll]);

  // Resolve a tenancy_id to its lead tenant_id so alerts keyed by tenancy
  // (s8_threshold, deposit_dispute) can navigate to the tenant detail page.
  const resolveTenantIdFromTenancy = async (tenancyId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/tenancies/${tenancyId}/tenants`, {
        headers: { 'Authorization': `Bearer ${API_KEY}` },
      });
      if (!res.ok) return null;
      const rows = JSON.parse((await res.text()) || '[]');
      if (!Array.isArray(rows) || rows.length === 0) return null;
      const lead = rows.find(r => r.is_lead_tenant) || rows[0];
      return lead?.id || lead?._id || null;
    } catch {
      return null;
    }
  };

  // Dashboard alert-panel click router — navigates to the correct view per
  // alert_type. Routing rules are intentionally strict (see spec); if a
  // reference_id is missing for a tenant-scoped alert, fall back to the
  // dashboard section rather than landing on an invalid URL.
  const handleAlertClick = async (alert) => {
    const t = alert.alert_type;
    const refId = alert.reference_id;

    // Pending message alerts — dashboard Pending Approvals section
    if (t === 'pending_message' || t === 'pending_4h' || t === 'pending_24h') {
      navigateToSection('pending-messages');
      return;
    }

    // Unknown contact alert — dashboard Unknown Contacts section
    if (t === 'unknown_whatsapp') {
      navigateToSection('whatsapp-alerts');
      return;
    }

    // 2026-06-05 — Contractor REPLY → dedicated triage panel
    // (NOT the generic maintenance detail modal). Uses
    // reference_secondary_id which is the ContractorOutreach id.
    if (t === 'contractor_reply') {
      const outreachId = alert.reference_secondary_id;
      if (outreachId) {
        setContractorTriageOutreachId(outreachId);
      } else if (refId) {
        // Older alerts without reference_secondary_id — fall back to
        // the maintenance detail modal so the click still does
        // something useful.
        setMaintDetailId(refId);
      }
      return;
    }

    // Maintenance alerts — navigate to the maintenance section on the
    // dashboard, then scroll the specific MR's kanban card into view
    // and apply a brief highlight animation (2026-06-09).
    //
    // 2026-02 (Bug 2 refix) — Pending-forward-generating alerts must
    // open the MR DETAIL modal directly (not just highlight the
    // kanban card), because the Approve/Edit/Dismiss controls live
    // inside `PendingMessagesSection` which only renders in the
    // detail view. Without this the alert was a dead-end for the
    // exact scenarios that promised "open the card to Approve /
    // Edit / Dismiss".
    const PENDING_FORWARD_ALERT_TYPES = new Set([
      'maintenance_tenant_note_v2',
      'maintenance_tenant_question_v2',
      'maintenance_tenant_note_queue_failed_v2',
      'maintenance_contractor_reply_unparsed_v2',
      'maintenance_reneg_contractor_reply_v2',
    ]);
    if (PENDING_FORWARD_ALERT_TYPES.has(t)) {
      setAlertsPanelOpen(false);
      if (refId) {
        // Set ?pane=pending BEFORE opening the modal so
        // PendingMessagesSection's mount-time scroll effect picks
        // it up and scrolls the queued action into view.
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('pane', 'pending');
          window.history.replaceState(null, '', url.toString());
        } catch (_e) { /* non-fatal */ }
        setMaintDetailId(refId);
      } else {
        navigateToSection('maintenance-board');
      }
      return;
    }

    // Issue 3 (2026-02) — Bug 4 alerts (tenant_auto_answered_v2)
    // and the deferred gate alerts (tenant_general_enquiry_v2,
    // tenant_lettings_enquiry_v2, tenant_viewing_request_v2,
    // maintenance_uncertain_review_v2) all deep-link to the
    // tenant's Communications tab (the actual conversation
    // thread), same convention as the pending-message dashboard
    // alerts. Item 0a (2026-02): fix — previous version linked to
    // ?tab=interactions but that tab does not exist on TenantDetail
    // (tabs are: communications, tenancy, case, compliance), so
    // the page opened on the default view instead of the thread.
    const BUG4_TENANT_ALERT_TYPES = new Set([
      'tenant_auto_answered_v2',
      'tenant_general_enquiry_v2',
      'tenant_lettings_enquiry_v2',
      'tenant_viewing_request_v2',
      'maintenance_uncertain_review_v2',
    ]);
    if (BUG4_TENANT_ALERT_TYPES.has(t)) {
      setAlertsPanelOpen(false);
      if (refId) {
        window.location.href =
          `/tenants/${refId}?tab=communications`;
      }
      return;
    }

    if (
      t === 'maintenance_new' ||
      t === 'maintenance_emergency' ||
      t === 'maintenance_contractor_escalation' ||
      t === 'maintenance_contractor_no_response' ||
      t === 'maintenance_no_contractor' ||
      t === 'maintenance_contractor_contacted' ||
      t === 'maintenance_visit_confirmed' ||
      t === 'maintenance_coordination_failed' ||
      // 2026-02 (iter 6) — v2 maintenance alert types.
      (typeof t === 'string' && t.startsWith('maintenance_') && t.endsWith('_v2'))
    ) {
      // Close the notifications dropdown so the user can see the card.
      setAlertsPanelOpen(false);
      if (refId) {
        setMaintHighlightMrId(refId);
      }
      navigateToSection('maintenance-board');
      return;
    }

    // New unknown-sender email enquiry — created by the IMAP poller when an
    // inbound email arrives from an address not matched to any Tenant
    // record. reference_id is the lead_id. Land on the lead detail page so
    // the agent can read the enquiry body, see Estatel AI AI triage progress,
    // and convert / archive the lead.
    if (t === 'new_unknown_email' || t === 'new_unknown_whatsapp') {
      if (refId) {
        window.location.href = `/leads/${refId}`;
      } else {
        navigateToSection('leads-section');
      }
      return;
    }

    // Known-tenant inbound email (2026-05-31) — reference_id is the
    // tenant_id. Land on the tenant detail page, Communications tab.
    if (t === 'tenant_email_inbound') {
      if (refId) {
        window.location.href = `/tenants/${refId}?tab=communications`;
      }
      return;
    }

    // 2026-02 (IMP 6 / IMP 7) — Tenant follow-up + Agent decision
    // required. Both alerts carry reference_id = tenant_id. The agent
    // can use the "Suggest follow-up wording" button on the alert
    // itself, but clicking the rest of the row should still land them
    // on the Tenant Detail page so they can see the full thread.
    if (t === 'tenant_followup_required' || t === 'agent_decision_required') {
      if (refId) {
        window.location.href = `/tenants/${refId}?tab=communications`;
      }
      return;
    }

    if (t === 'compliance_unclassified_doc') {
      if (refId) setClassifyAlert(alert);
      return;
    }

    // Compliance & licence alerts — dashboard Compliance Status section
    if (t === 'compliance_expired' || t === 'licence_expired' || t === 'licence_expiring') {
      navigateToSection('compliance-status');
      return;
    }

    // Section 8 threshold — tenant detail page, Tenancy tab (reference_id is a tenancy_id).
    // Arrears entry points always land on the Tenancy tab so the agent sees
    // the arrears history, guidance banner, Log Payment and Start S8 buttons.
    if (t === 's8_threshold') {
      if (!refId) { navigateToSection('arrears-overview'); return; }
      const tid = await resolveTenantIdFromTenancy(refId);
      if (tid) {
        window.location.href = `/tenants/${tid}?tab=tenancy`;
      } else {
        navigateToSection('arrears-overview');
      }
      return;
    }

    // Deposit dispute — tenant detail page, Tenancy tab (reference_id is tenancy_id)
    if (t === 'deposit_dispute') {
      if (!refId) { navigateToSection('active-cases'); return; }
      const tid = await resolveTenantIdFromTenancy(refId);
      if (tid) {
        window.location.href = `/tenants/${tid}?tab=tenancy`;
      }
      return;
    }

    // Court deadline & hearing — Section 8 case page (reference_id is case_id)
    if (t === 'court_deadline_48h' || t === 'hearing_48h') {
      if (refId) {
        window.location.href = `/section8/case/${refId}`;
      } else {
        navigateToSection('active-cases');
      }
      return;
    }

    // Section 8 notice service-window alerts — Section 8 case page so the
    // agent can log the date of service (Stage 3) or re-issue the notice if
    // the 48-hour service window has expired. reference_id is case_id.
    if (
      t === 's8_notice_generated' ||
      t === 's8_notice_24h' ||
      t === 's8_notice_48h' ||
      t === 's8_notice_overdue'
    ) {
      if (refId) {
        window.location.href = `/section8/case/${refId}`;
      } else {
        navigateToSection('active-cases');
      }
      return;
    }
  };
  
  const handleLinkToTenant = async (alertId, tenantId) => {
    try {
      const headers = {
        'Authorization': `Bearer ${API_KEY}`,
        'Content-Type': 'application/json'
      };
      
      const response = await fetch(
        `${API_BASE_URL}/api/whatsapp/alerts/${alertId}/link`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ tenant_id: tenantId })
        }
      );
      
      if (!response.ok) throw new Error('Failed to link alert to tenant');
      
      alert('✅ Contact linked to existing tenant successfully!');
      await fetchDashboardData();
    } catch (err) {
      alert('Error linking contact: ' + err.message);
    }
  };
  
  const handleCreateTenant = async (alertId, firstName, lastName, email) => {
    try {
      const headers = {
        'Authorization': `Bearer ${API_KEY}`,
        'Content-Type': 'application/json'
      };
      
      const response = await fetch(
        `${API_BASE_URL}/api/whatsapp/alerts/${alertId}/create`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            first_name: firstName,
            last_name: lastName,
            email: email || null
          })
        }
      );
      
      if (!response.ok) throw new Error('Failed to create tenant');
      
      const data = await response.json();
      alert(`✅ New tenant created successfully! Tenant ID: ${data.tenant_id}`);
      await fetchDashboardData();
    } catch (err) {
      alert('Error creating tenant: ' + err.message);
    }
  };
  
  const handleOnboardingComplete = () => {
    setCurrentView('dashboard');
    // Refresh dashboard data after onboarding
    fetchDashboardData();
  };
  
  // Duplicate alert resolution
  const openResolveView = async (alert) => {
    setResolvingAlert(alert);
    setResolveLoading(true);
    try {
      const headers = {
        'Authorization': `Bearer ${API_KEY}`,
        'Content-Type': 'application/json'
      };
      const res = await fetch(`${API_BASE_URL}/api/alerts/duplicates/${alert._id}/details`, { headers });
      if (res.ok) {
        const data = await res.json();
        setResolveDetails(data);
      }
    } catch (err) {
      console.error('Error fetching duplicate details:', err);
    } finally {
      setResolveLoading(false);
    }
  };
  
  const handleResolveDuplicate = async (alertId, action, notes) => {
    try {
      const headers = {
        'Authorization': `Bearer ${API_KEY}`,
        'Content-Type': 'application/json'
      };
      const res = await fetch(`${API_BASE_URL}/api/alerts/duplicates/${alertId}/resolve`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action, notes })
      });
      if (res.ok) {
        setResolvingAlert(null);
        setResolveDetails(null);
        await fetchDashboardData();
      } else {
        alert('Failed to resolve duplicate alert');
      }
    } catch (err) {
      alert('Error resolving duplicate: ' + err.message);
    }
  };
  

  const handleDismissWhatsAppAlert = async (alertId) => {
    try {
      await fetch(`${API_BASE_URL}/api/whatsapp/alerts/${alertId}/dismiss`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${API_KEY}`, 'Content-Type': 'application/json' },
      });
      fetchDashboardData();
    } catch {}
  };

  const handleDismissDuplicate = async (alertId) => {
    try {
      await fetch(`${API_BASE_URL}/api/alerts/duplicates/${alertId}/resolve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${API_KEY}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'dismiss', notes: 'Not a duplicate — dismissed by agent' }),
      });
      fetchDashboardData();
    } catch {}
  };

  const getDeadlineClass = (deadline) => {
    if (!deadline) return '';
    const deadlineDate = new Date(deadline);
    const today = new Date();
    const daysUntil = Math.ceil((deadlineDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntil <= 2) return 'deadline-critical';
    if (daysUntil <= 7) return 'deadline-warning';
    return '';
  };
  
  if (loading) {
    return (
      <div className="app">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="app">
        <div className="error-container">
          <h2>Error Loading Dashboard</h2>
          <p>{error}</p>
          <button onClick={fetchDashboardData} className="btn-primary" style={{ background: '#0073EA', color: '#fff' }}>Retry</button>
        </div>
      </div>
    );
  }
  
  const pendingCount = pendingMessages.length;
  const activeCasesCount = activeCases.length;
  // 2026-02 (Item 6) — the Compliance badge scrolls to the dashboard
  // Compliance section, which only surfaces `expiring_docs`. Including
  // the structural compliance-scanner red/amber count here made the
  // badge show "19" while the section itself read "All compliance
  // documents are up to date" — confusing. The badge now matches the
  // destination section: count of docs expiring/expired only.
  // Scanner red/amber counts remain available on the dedicated
  // Compliance Scanner page; they no longer inflate this badge.
  const complianceAlertsCount = (digestSummary?.expiring_docs?.length || 0);
  const arrearsCount = digestSummary?.arrears_summary?.tenant_count || 0;
  const whatsappAlertsCount = whatsappAlerts.length;
  const duplicateAlertsCount = duplicateAlerts.length;
  const availablePropertiesCount = availableProperties.length;
  
  
  // Avatar / user-menu props (shared across every header)
  const avatarInitials = (billingInfo?.company_name || 'EA')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(s => s[0])
    .join('')
    .toUpperCase() || 'EA';

  const avatarProps = {
    initials: avatarInitials,
    agentName: billingInfo?.company_name,
    agentEmail: billingInfo?.contact_email,
    onGoToSettings: () => setCurrentView('settings'),
    onGoToAdmin: billingInfo?.is_owner_admin ? () => { window.location.href = '/admin'; } : undefined,
    onOpenSupport: () => setSupportOpen(true),
    // Real session sign-out: clears the httpOnly cookie via /api/auth/logout
    // and bounces the agent back to /login.
    onSignOut: async () => {
      try { await authLogout(); } catch { /* noop */ }
      window.location.href = '/login';
    },
  };

  // Sidebar counts bundled once — same object is reused across every sidebar
  const sidebarCounts = {
    pending: pendingCount,
    whatsapp: whatsappAlertsCount,
    duplicates: duplicateAlertsCount,
    maintenance: maintenanceCounts.open_count || 0,
    compliance: complianceAlertsCount,
    available: availablePropertiesCount,
    // 2026-05-29 (CHANGE 1) — Count of currently-let properties for the
    // new Properties → Let sidebar sub-item.
    let: (digestSummary?.let_properties_count || 0),
    leads: leadCounts.new || 0,
    arrears: arrearsCount,
    section8: activeCasesCount,
  };

  // Reusable sidebar props. `onNav` is a scrollable section id.
  const sidebarPropsDashboard = {
    counts: sidebarCounts,
    activeView: 'dashboard',
    onNav: scrollToSection,
    onDashboard: () => setCurrentView('dashboard'),
    onLandlords: () => { setSettingsInitialTab('landlords'); setCurrentView('landlords'); },
    onProperties: () => setCurrentView('properties'),
    onPropertiesAvailable: () => setCurrentView('properties'),
    onPropertiesLet: () => setCurrentView('properties-let'),
    onContractors: () => setCurrentView('contractors'),
    onOnboarding: () => setCurrentView('onboarding'),
    showOnboarding: setupAllClear === false,
    onSettings: () => { setSettingsInitialTab(null); setCurrentView('settings'); },
  };
  const sidebarPropsCrossView = (activeView) => ({
    counts: sidebarCounts,
    activeView,
    onNav: navigateToSection,
    onDashboard: () => setCurrentView('dashboard'),
    onLandlords: () => { setSettingsInitialTab('landlords'); setCurrentView('landlords'); },
    onProperties: () => setCurrentView('properties'),
    onPropertiesAvailable: () => setCurrentView('properties'),
    onPropertiesLet: () => setCurrentView('properties-let'),
    onContractors: () => setCurrentView('contractors'),
    onOnboarding: () => setCurrentView('onboarding'),
    showOnboarding: setupAllClear === false,
    onSettings: () => { setSettingsInitialTab(null); setCurrentView('settings'); },
  });

  // Step 45 — "landlords" top-level view reuses the existing Settings
  // Landlords tab content (full CRUD) by rendering Settings with the tab
  // pre-selected. Avoids duplicating 300 lines of working UI.
  if (currentView === 'landlords') {
    return (
      <div className="app">
        <header className="eiq-header">
          <div className="eiq-header-brand"><img src={estatelLogo} alt="estatel" className="eiq-brand-logo" /></div>
          <div className="eiq-header-center">
            <div className="navbar-search" ref={searchRef} data-testid="tenant-search-container">
              <input type="text" className="search-input" placeholder="Search..." value={searchQuery} onChange={e => handleSearchChange(e.target.value)} data-testid="tenant-search-input" />
            </div>
          </div>
          <div className="eiq-header-right"><AvatarMenu {...avatarProps} /></div>
        </header>
        <Sidebar {...sidebarPropsCrossView('landlords')} />
        <main className="eiq-main"><Settings initialTab="landlords" hideTabs={true} /></main>
      </div>
    );
  }

  // If viewing onboarding, render it instead of dashboard
  if (currentView === 'onboarding') {
    return <Onboarding onComplete={handleOnboardingComplete} />;
  }

  // 2026-06-04 — Contractors dashboard (Part 2).
  if (currentView === 'contractors') {
    return (
      <div className="app">
        <header className="eiq-header">
          <div className="eiq-header-brand"><img src={estatelLogo} alt="estatel" className="eiq-brand-logo" /></div>
          <div className="eiq-header-center">
            <div className="navbar-search" ref={searchRef} data-testid="tenant-search-container">
              <input type="text" className="search-input" placeholder="Search..." value={searchQuery} onChange={e => handleSearchChange(e.target.value)} data-testid="tenant-search-input" />
            </div>
          </div>
          <div className="eiq-header-right"><AvatarMenu {...avatarProps} /></div>
        </header>
        <Sidebar {...sidebarPropsCrossView('contractors')} />
        <main className="eiq-main"><Contractors /></main>
      </div>
    );
  }
  
  // If viewing settings, render it instead of dashboard
  if (currentView === 'settings') {
    return (
      <div className="app app-settings-fullwidth">
        <header className="eiq-header">
          <div className="eiq-header-brand"><img src={estatelLogo} alt="estatel" className="eiq-brand-logo" /></div>
          <div className="eiq-header-center">
            <div className="navbar-search" ref={searchRef} data-testid="tenant-search-container">
              <input type="text" className="search-input" placeholder="Search..." value={searchQuery} onChange={e => handleSearchChange(e.target.value)} data-testid="tenant-search-input" />
            </div>
          </div>
          <div className="eiq-header-right"><AvatarMenu {...avatarProps} /></div>
        </header>
        {/* 2026-02 — Settings page rendered full-width (no sidebar) so the
            tabbed nav fits without horizontal scrolling. A prominent
            "Back to Dashboard" button replaces sidebar navigation. */}
        <main className="eiq-main eiq-main-no-sidebar" style={{ marginLeft: 0, width: '100%' }}>
          <div style={{ padding: '1rem 1.5rem 0' }}>
            <button
              type="button"
              onClick={() => setCurrentView('dashboard')}
              data-testid="settings-back-to-dashboard"
              style={{ background: '#0073EA', color: '#fff', border: 'none',
                       borderRadius: 8, padding: '0.55rem 1.1rem',
                       fontWeight: 700, fontSize: '0.88rem',
                       cursor: 'pointer', display: 'inline-flex',
                       alignItems: 'center', gap: 6,
                       boxShadow: '0 2px 6px rgba(0,115,234,0.25)' }}>
              &larr; Back to Dashboard
            </button>
          </div>
          <Settings initialTab={settingsInitialTab} onOpenOnboarding={() => setCurrentView('onboarding')} />
        </main>
      </div>
    );
  }
  
  // If viewing tenants list
  if (currentView === 'properties') {
    return (
      <div className="app">
        <header className="eiq-header">
          <div className="eiq-header-brand"><img src={estatelLogo} alt="estatel" className="eiq-brand-logo" /></div>
          <div className="eiq-header-center">
            <div className="navbar-search" ref={searchRef} data-testid="tenant-search-container">
              <input type="text" className="search-input" placeholder="Search..." value={searchQuery} onChange={e => handleSearchChange(e.target.value)} data-testid="tenant-search-input" />
            </div>
          </div>
          <div className="eiq-header-right"><AvatarMenu {...avatarProps} /></div>
        </header>
        <Sidebar {...sidebarPropsCrossView('properties')} />
        <main className="eiq-main">
          <PropertiesList initialView="to_let" />
        </main>
      </div>
    );
  }

  // CHANGE 1 — Properties → Let sub-view.
  if (currentView === 'properties-let') {
    return (
      <div className="app">
        <header className="eiq-header">
          <div className="eiq-header-brand"><img src={estatelLogo} alt="estatel" className="eiq-brand-logo" /></div>
          <div className="eiq-header-center">
            <div className="navbar-search" ref={searchRef} data-testid="tenant-search-container">
              <input type="text" className="search-input" placeholder="Search..." value={searchQuery} onChange={e => handleSearchChange(e.target.value)} data-testid="tenant-search-input" />
            </div>
          </div>
          <div className="eiq-header-right"><AvatarMenu {...avatarProps} /></div>
        </header>
        <Sidebar {...sidebarPropsCrossView('properties-let')} />
        <main className="eiq-main">
          <PropertiesList initialView="let" />
        </main>
      </div>
    );
  }

  if (currentView === 'tenants') {
    return (
      <div className="app">
        <header className="eiq-header">
          <div className="eiq-header-brand"><img src={estatelLogo} alt="estatel" className="eiq-brand-logo" /></div>
          <div className="eiq-header-center">
            <div className="navbar-search" ref={searchRef} data-testid="tenant-search-container">
              <input type="text" className="search-input" placeholder="Search..." value={searchQuery} onChange={e => handleSearchChange(e.target.value)} onFocus={() => { if (searchQuery.length >= 2) setSearchOpen(true); }} data-testid="tenant-search-input" />
              {searchOpen && (
                <div className="search-dropdown" data-testid="search-dropdown">
                  {searchLoading ? <div className="search-loading">Searching...</div> : searchResults.length === 0 ? <div className="search-empty">No tenants found</div> : searchResults.map(r => (
                    <Link key={r.id} to={`/tenants/${r.id}`} className="search-result-item" onClick={() => { setSearchQuery(''); setSearchOpen(false); }}><div className="sr-name">{r.first_name} {r.last_name}</div></Link>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="eiq-header-right"><AvatarMenu {...avatarProps} /></div>
        </header>
        <Sidebar {...sidebarPropsCrossView('tenants')} />
        <main className="eiq-main">
          <section className="dashboard-section">
            <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h2 className="section-title">All Tenants</h2>
                <p className="section-subtitle">Click a tenant name to view their full detail page</p>
              </div>
              {allTenants && allTenants.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowAddTenantModal(true)}
                  style={{
                    background: '#0073EA', color: '#fff', border: 'none',
                    padding: '0.55rem 1rem', borderRadius: 6, fontSize: '0.9rem',
                    fontWeight: 500, cursor: 'pointer',
                  }}
                  data-testid="btn-add-tenant-header"
                >
                  + Add Tenant
                </button>
              )}
            </div>
            {allTenants && allTenants.length > 0 ? (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Tenant Name</th>
                      <th>Property</th>
                      <th>Phone</th>
                      <th>Right to Rent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleTenants.map((t, idx) => (
                      <tr key={t.id || idx}>
                        <td className="cell-bold"><Link to={`/tenants/${t.id || t.tenant_id}`} className="tenant-link">{t.name || t.tenant_name}</Link></td>
                        <td>
                          {(t.property_id || t.propertyId) ? (
                            <Link to={`/properties/${t.property_id || t.propertyId}`}
                                  style={{ color: '#0073EA', textDecoration: 'none' }}>
                              {t.property || t.property_address || ''}
                            </Link>
                          ) : (t.property || t.property_address || '')}
                        </td>
                        <td>{t.phone || ''}</td>
                        <td><span className={`rtr-list-badge rtr-list-${t.rtr_status || 'red'}`} data-testid={`rtr-list-badge-${idx}`}>{t.rtr_text || 'Not checked'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <SeeMoreButton hasMore={tenantsPager.hasMore} remaining={tenantsPager.remaining} onClick={tenantsPager.showMore} testid="tenants-list" />
              </div>
            ) : (
              <EmptyStatePanel
                testid="tenants-empty-state"
                title="No tenants added yet."
                description="Add tenants manually or import your entire portfolio through the setup wizard."
                actions={[
                  { label: 'Add Tenant', testid: 'btn-add-tenant-manual', onClick: () => setShowAddTenantModal(true) },
                  { label: 'Import via Setup Wizard', testid: 'btn-import-via-wizard', variant: 'secondary', onClick: () => setCurrentView('onboarding') },
                ]}
              />
            )}
            {showAddTenantModal && (
              <AddTenantModal
                onClose={() => setShowAddTenantModal(false)}
                onCreated={() => { setShowAddTenantModal(false); fetchAllTenants(); }}
              />
            )}
          </section>
        </main>
      </div>
    );
  }
  
  // If resolving a duplicate, show the side-by-side view
  if (resolvingAlert) {
    return (
      <div className="app">
        <header className="eiq-header">
          <div className="eiq-header-brand"><img src={estatelLogo} alt="estatel" className="eiq-brand-logo" /></div>
          <div className="eiq-header-center"></div>
          <div className="eiq-header-right"><AvatarMenu {...avatarProps} /></div>
        </header>
        <nav className="eiq-sidebar">
          <div className="eiq-sidebar-section">
            <button className="eiq-nav-item" onClick={() => { setResolvingAlert(null); setResolveDetails(null); setCurrentView('dashboard'); }} data-testid="nav-dashboard">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>Dashboard
            </button>
          </div>
        </nav>
        <main className="eiq-main">
          <div className="resolve-page">
            <button className="resolve-back-btn" onClick={() => { setResolvingAlert(null); setResolveDetails(null); }} data-testid="btn-back-from-resolve">
              &larr; Back to Dashboard
            </button>
            <h2 className="resolve-heading">Resolve Duplicate Contact</h2>
            <div className="resolve-conflict-bar">
              <span>Conflict: {resolvingAlert.conflict_type === 'phone_and_email' ? 'Phone and email map to different tenants' : resolvingAlert.conflict_type?.replace(/_/g, ' ')}</span>
              {resolvingAlert.conflicting_phone && <span className="resolve-conflict-tag">Phone: {resolvingAlert.conflicting_phone}</span>}
              {resolvingAlert.conflicting_email && <span className="resolve-conflict-tag">Email: {resolvingAlert.conflicting_email}</span>}
            </div>

            {resolveLoading ? (
              <div className="loading-container"><div className="loading-spinner"></div><p>Loading tenant details...</p></div>
            ) : resolveDetails ? (
              <>
                <div className="resolve-cards">
                  {/* Tenant A */}
                  <div className="resolve-card" data-testid="resolve-tenant-1">
                    <div className="rc-header rc-header-a">Contact Record A</div>
                    <div className="rc-body">
                      <div className="rc-field"><span className="rc-label">Name</span><span className="rc-value">{resolveDetails.tenant_1?.first_name} {resolveDetails.tenant_1?.last_name}</span></div>
                      <div className="rc-field"><span className="rc-label">Email</span><span className="rc-value">{resolveDetails.tenant_1?.email || 'N/A'}</span></div>
                      <div className="rc-field"><span className="rc-label">Phone</span><span className="rc-value">{resolveDetails.tenant_1?.phone_number || 'N/A'}</span></div>
                      <div className="rc-field"><span className="rc-label">Interactions</span><span className="rc-value">{resolveDetails.interaction_count_1}</span></div>
                      {resolveDetails.tenancy_1 && (
                        <>
                          <div className="rc-field"><span className="rc-label">Rent</span><span className="rc-value">{formatCurrency(resolveDetails.tenancy_1.rent_amount)}</span></div>
                          <div className="rc-field"><span className="rc-label">Tenancy</span><span className="rc-value">{resolveDetails.tenancy_1.status}</span></div>
                        </>
                      )}
                      <div className="rc-field"><span className="rc-label">Created</span><span className="rc-value">{formatDate(resolveDetails.tenant_1?.created_at)}</span></div>
                    </div>
                  </div>

                  <div className="resolve-vs-divider">VS</div>

                  {/* Tenant B */}
                  <div className="resolve-card" data-testid="resolve-tenant-2">
                    <div className="rc-header rc-header-b">Contact Record B</div>
                    <div className="rc-body">
                      <div className="rc-field"><span className="rc-label">Name</span><span className="rc-value">{resolveDetails.tenant_2?.first_name} {resolveDetails.tenant_2?.last_name}</span></div>
                      <div className="rc-field"><span className="rc-label">Email</span><span className="rc-value">{resolveDetails.tenant_2?.email || 'N/A'}</span></div>
                      <div className="rc-field"><span className="rc-label">Phone</span><span className="rc-value">{resolveDetails.tenant_2?.phone_number || 'N/A'}</span></div>
                      <div className="rc-field"><span className="rc-label">Interactions</span><span className="rc-value">{resolveDetails.interaction_count_2}</span></div>
                      {resolveDetails.tenancy_2 && (
                        <>
                          <div className="rc-field"><span className="rc-label">Rent</span><span className="rc-value">{formatCurrency(resolveDetails.tenancy_2.rent_amount)}</span></div>
                          <div className="rc-field"><span className="rc-label">Tenancy</span><span className="rc-value">{resolveDetails.tenancy_2.status}</span></div>
                        </>
                      )}
                      <div className="rc-field"><span className="rc-label">Created</span><span className="rc-value">{formatDate(resolveDetails.tenant_2?.created_at)}</span></div>
                    </div>
                  </div>
                </div>

                <div className="resolve-action-bar" data-testid="resolve-actions">
                  <button className="btn-secondary resolve-action-btn" onClick={() => handleResolveDuplicate(resolvingAlert._id, 'keep_both', 'Agent chose to keep both records')} data-testid="btn-keep-both">Keep Both</button>
                  <button className="btn-approve resolve-action-btn" onClick={() => handleResolveDuplicate(resolvingAlert._id, 'merge_into_1', 'Agent chose to merge into Record A')} data-testid="btn-merge-into-1">Merge &mdash; Keep A</button>
                  <button className="btn-approve resolve-action-btn" onClick={() => handleResolveDuplicate(resolvingAlert._id, 'merge_into_2', 'Agent chose to merge into Record B')} data-testid="btn-merge-into-2">Merge &mdash; Keep B</button>
                  <button className="btn-reject resolve-action-btn" onClick={() => handleResolveDuplicate(resolvingAlert._id, 'delete_1', 'Agent chose to delete Record A')} data-testid="btn-delete-1">Delete A</button>
                  <button className="btn-reject resolve-action-btn" onClick={() => handleResolveDuplicate(resolvingAlert._id, 'delete_2', 'Agent chose to delete Record B')} data-testid="btn-delete-2">Delete B</button>
                </div>
              </>
            ) : (
              <div className="empty-state"><p>Failed to load tenant details</p></div>
            )}
          </div>
        </main>
      </div>
    );
  }
  
  return (
    <div className="app">
      {/* ===== TOP HEADER ===== */}
      <header className="eiq-header">
        <div className="eiq-header-brand">
          <img src={estatelLogo} alt="estatel" className="eiq-brand-logo" />
        </div>
        <div className="eiq-header-center">
          <div className="navbar-search" ref={searchRef} data-testid="tenant-search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Search..."
              value={searchQuery}
              onChange={e => handleSearchChange(e.target.value)}
              onFocus={() => { if (searchQuery.length >= 2) setSearchOpen(true); }}
              data-testid="tenant-search-input"
            />
            {searchOpen && (
              <div className="search-dropdown" data-testid="search-dropdown">
                {searchLoading ? (
                  <div className="search-loading">Searching...</div>
                ) : searchResults.length === 0 ? (
                  <div className="search-empty" data-testid="search-no-results">No tenants found</div>
                ) : (
                  searchResults.map(r => (
                    <Link
                      key={r.id}
                      to={`/tenants/${r.id}`}
                      className="search-result-item"
                      onClick={() => { setSearchQuery(''); setSearchOpen(false); }}
                      data-testid={`search-result-${r.id}`}
                    >
                      <div className="sr-name">{r.first_name} {r.last_name}</div>
                      <div className="sr-meta">
                        <span className="sr-address">{r.property_address || 'No property'}</span>
                        <span className={`sr-badge ${r.tenancy_status === 'active' ? 'badge-green' : r.tenancy_status === 'eviction_in_progress' ? 'badge-red' : 'badge-grey'}`}>
                          {r.tenancy_status === 'active' ? 'Active' : r.tenancy_status === 'eviction_in_progress' ? 'Eviction' : 'Ended'}
                        </span>
                      </div>
                    </Link>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
        <div className="eiq-header-right">
          <button className="eiq-notif-btn" onClick={() => setAlertsPanelOpen(!alertsPanelOpen)} data-testid="header-notif-btn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>
            {realtimeAlerts.length > 0 && <span className="eiq-notif-badge">{realtimeAlerts.length}</span>}
          </button>
          <AvatarMenu {...avatarProps} />
        </div>
      </header>

      {/* ===== LEFT SIDEBAR ===== */}
      <Sidebar {...sidebarPropsDashboard} />

      {/* ===== MAIN CONTENT ===== */}
      <main className="eiq-main">

      {/* Subscription Banners (Main Dashboard) — legacy trial strip removed
          in favour of the richer <TrialBanner /> below (ENHANCEMENT 3).
          Wrapping group controls vertical spacing without being doubled
          by `.eiq-main { gap: 1.5rem }`. */}
      <div className="dashboard-banners" style={{ display: 'flex',
           flexDirection: 'column', gap: '0.5rem', margin: 0 }}>
      {billingInfo?.subscription_status === 'past_due' && (
        <div className="sub-banner sub-banner-danger" data-testid="past-due-banner">
          <strong>Payment failed.</strong> Please update your payment method to avoid service interruption.
          {billingInfo.grace_expired && ' Your account is now in read-only mode.'}
          <button className="sub-banner-btn sub-banner-btn-danger" onClick={() => setCurrentView('settings')} data-testid="btn-fix-payment">Fix Payment</button>
        </div>
      )}
      {billingInfo?.subscription_status === 'cancelled' && (
        <div className="sub-banner sub-banner-danger" data-testid="cancelled-banner">
          <strong>Subscription cancelled.</strong> Your account is in read-only mode. Upgrade to restore full access.
          <button className="sub-banner-btn sub-banner-btn-danger" onClick={() => setCurrentView('settings')} data-testid="btn-resubscribe">Resubscribe</button>
        </div>
      )}

      {/* ENHANCEMENT 3 — trial countdown banner (first, top of dashboard). */}
      <TrialBanner
        onGoToBilling={() => {
          setSettingsInitialTab('billing');
          setCurrentView('settings');
        }}
      />

      {/* Step 41/45 — Post-login setup checklist banner */}
      <SetupChecklistBanner
        onItemClick={(itemId) => {
          if (itemId === 'email') {
            setSettingsInitialTab('email');
            setCurrentView('settings');
          } else if (itemId === 'whatsapp') {
            setSettingsInitialTab('integrations');
            setCurrentView('settings');
          } else if (itemId === 'landlord') {
            setSettingsInitialTab('landlords');
            setCurrentView('settings');
          } else if (itemId === 'referencing') {
            setSettingsInitialTab('pre_tenancy');
            setCurrentView('settings');
          } else if (itemId === 'compliance') {
            // Compliance upload lives on the dashboard itself — scroll to it.
            setCurrentView('dashboard');
            setPendingScroll('compliance-status');
          }
        }}
      />
      {/* Set Up wizard entry — moved to Settings (2026-06-01).
          Dashboard no longer surfaces this button so concierge-
          onboarded agents don't see an unwanted CTA. */}
      {/* 2026-02 — Concierge Onboarding amber banner (renders only when
          `onboarding_status === 'concierge_in_progress'` on AgentClient). */}
      <ConciergeBanner />
      {/* ENHANCEMENT 5 — demo-data banner (purple outline variant). */}
      <DemoDataBanner />
      {/* FIX 3 (onboarding) — amber reminder for properties without a linked landlord */}
      <PropertiesWithoutLandlordBanner
        onGoToSettings={() => {
          setSettingsInitialTab('landlords');
          setCurrentView('settings');
        }}
      />
      </div>
      
      {/* Real-Time Notification Alerts Panel */}
      {realtimeAlerts.length > 0 && (
        <div className="realtime-alerts-panel" data-testid="realtime-alerts-panel">
          <div className="realtime-alerts-header">
            <span className="realtime-alerts-title" data-testid="realtime-alerts-count">
              {realtimeAlerts.length} Active Live Feed{realtimeAlerts.length !== 1 ? '' : ''}
            </span>
            <button
              className="realtime-alerts-toggle"
              onClick={() => setAlertsPanelOpen(!alertsPanelOpen)}
              data-testid="realtime-alerts-toggle"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{transform: alertsPanelOpen ? 'rotate(0deg)' : 'rotate(-90deg)', transition:'transform 0.2s'}}><polyline points="6 9 12 15 18 9"/></svg>
              {alertsPanelOpen ? 'Collapse' : 'Expand'}
            </button>
          </div>
          {alertsPanelOpen && (
            <AlertsPanelErrorBoundary>
            <div className="realtime-alerts-list" data-testid="realtime-alerts-list">
              {realtimeAlerts.slice(0, 20).map(alert => {
                // 2026-02 (iter 14, Issue 1b) — defensive null-guards
                // on every field the render code touches. A missing
                // field can no longer throw (would previously crash
                // the entire panel via ".replace() on undefined").
                if (!alert || typeof alert !== 'object') return null;
                const safeAlertId = alert._id || `unknown-${Math.random().toString(36).slice(2)}`;
                const safeType = alert.alert_type || 'unknown';
                const safeTitle = decodeText(alert.title || '') || '(untitled alert)';
                const safeBody = decodeText(alert.body || '');
                const safeLevel = alert.level || 'info';
                const style = ALERT_LEVEL_STYLES[safeLevel] || ALERT_LEVEL_STYLES.info;
                const typeLabel = ALERT_TYPE_LABELS[safeType] || safeType;
                const relativeTime = (() => {
                  if (!alert.created_at) return '';
                  const t = new Date(alert.created_at).getTime();
                  if (Number.isNaN(t)) return '';
                  const diff = Date.now() - t;
                  const mins = Math.floor(diff / 60000);
                  if (mins < 60) return `${mins}m ago`;
                  const hrs = Math.floor(mins / 60);
                  if (hrs < 24) return `${hrs}h ago`;
                  return `${Math.floor(hrs / 24)}d ago`;
                })();
                return (
                  <div
                    key={safeAlertId}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleAlertClick(alert)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleAlertClick(alert); } }}
                    className="realtime-alert-item realtime-alert-item-clickable"
                    style={{ background: style.bg, borderLeft: `4px solid ${style.border}` }}
                    data-testid={`realtime-alert-${safeAlertId}`}
                  >
                    <div className="realtime-alert-content">
                      <div className="realtime-alert-top">
                        <span className="realtime-alert-level" style={{ background: style.badgeBg, color: style.badgeText }} data-testid={`alert-level-${safeAlertId}`}>
                          {style.label}
                        </span>
                        <span className="realtime-alert-type">{typeLabel}</span>
                        {alert.follow_up_count > 0 && (
                          <span
                            className="realtime-alert-followup-pill"
                            data-testid={`alert-followup-pill-${safeAlertId}`}
                            style={{
                              background: '#fef3c7',  // amber-100
                              color: '#92400e',       // amber-800
                              border: '1px solid #fcd34d', // amber-300
                              padding: '2px 8px',
                              borderRadius: '9999px',
                              fontSize: '0.72rem',
                              fontWeight: 600,
                              marginLeft: '0.4rem',
                              lineHeight: 1.3,
                            }}
                            title={`${alert.follow_up_count} follow-up${alert.follow_up_count === 1 ? '' : 's'} from this contact since escalation`}
                          >
                            +{alert.follow_up_count} new
                          </span>
                        )}
                        <span className="realtime-alert-time">{relativeTime}</span>
                      </div>
                      <div className="realtime-alert-title" style={{ color: style.color }} data-testid={`alert-title-${safeAlertId}`}>
                        {safeTitle.replace(/\s*·\s*\d+\s+follow-ups?\s+awaiting\s+reply\s*$/i, '')}
                      </div>
                      <div className="realtime-alert-body" data-testid={`alert-body-${safeAlertId}`}>
                        {safeBody}
                      </div>
                      {(safeType === 'tenant_followup_required' || safeType === 'agent_decision_required') && alert.reference_id && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); openFollowupSuggest(alert); }}
                          data-testid={`btn-suggest-followup-${alert._id}`}
                          style={{
                            marginTop: 8,
                            background: '#4338ca', color: '#fff',
                            border: 'none', borderRadius: 6,
                            padding: '0.35rem 0.75rem', fontSize: '0.78rem',
                            fontWeight: 600, cursor: 'pointer',
                          }}>
                          Suggest follow-up wording
                        </button>
                      )}
                      {/* 2026-08-08 — Structured-metadata actions
                          on alerts. When an alert carries
                          `structured_metadata.actions[]`, render
                          each action as a button. Used by
                          `maintenance_split_from_booked_v2` for
                          one-click merge; forward-compatible with
                          any future alert type. */}
                      {alert.structured_metadata &&
                        Array.isArray(alert.structured_metadata.actions) &&
                        alert.structured_metadata.actions.length > 0 && (
                        <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}
                             data-testid={`alert-actions-${alert._id}`}>
                          {alert.structured_metadata.actions.map((act, idx) => {
                            const isPrimary = act.style === 'primary';
                            return (
                              <button
                                key={act.id || idx}
                                type="button"
                                data-testid={`alert-action-${act.id || idx}-${alert._id}`}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (act.confirm && !window.confirm(act.confirm)) return;
                                  if (act.method === 'NAV') {
                                    window.location.hash = act.url.replace(/^\/(?=[^#])/, '');
                                    return;
                                  }
                                  try {
                                    const r = await fetch(
                                      `${API_BASE_URL}${act.url}`,
                                      { method: act.method || 'POST',
                                        credentials: 'include',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({}) },
                                    );
                                    if (!r.ok) throw new Error(await r.text());
                                    setRealtimeAlerts(prev => prev.filter(a => a._id !== alert._id));
                                  } catch (err) {
                                    window.alert('Action failed: ' + (err.message || 'unknown'));
                                  }
                                }}
                                style={{
                                  background: isPrimary ? '#0073EA' : '#e2e8f0',
                                  color: isPrimary ? '#fff' : '#0f172a',
                                  border: 'none', borderRadius: 6,
                                  padding: '0.32rem 0.7rem', fontSize: '0.75rem',
                                  fontWeight: 600, cursor: 'pointer',
                                }}>
                                {act.label}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {alert.alert_type === 'business_email_received' && (
                        <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}
                             data-testid={`bizmail-actions-${alert._id}`}>
                          <button type="button"
                                  onClick={(e) => { e.stopPropagation(); setBizEmailAlert(alert); }}
                                  data-testid={`bizmail-view-${alert._id}`}
                                  style={{ background: '#0073EA', color: '#fff',
                                           border: 'none', borderRadius: 6,
                                           padding: '0.32rem 0.7rem', fontSize: '0.75rem',
                                           fontWeight: 600, cursor: 'pointer' }}>
                            View Email
                          </button>
                          <button type="button"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    if (!window.confirm('Mark this contact as a Lead and route through the standard triage?')) return;
                                    try {
                                      const r = await fetch(`${API_BASE_URL}/api/business-email/${alert._id}/mark-as-lead`, { method: 'POST', credentials: 'include' });
                                      if (!r.ok) throw new Error();
                                      const d = await r.json();
                                      setRealtimeAlerts(prev => prev.filter(a => a._id !== alert._id));
                                      setBizFlash(d.message || 'Contact moved to leads.');
                                      setTimeout(() => setBizFlash(''), 4000);
                                    } catch { window.alert('Could not mark as lead.'); }
                                  }}
                                  data-testid={`bizmail-mark-${alert._id}`}
                                  style={{ background: '#f59e0b', color: '#fff',
                                           border: 'none', borderRadius: 6,
                                           padding: '0.32rem 0.7rem', fontSize: '0.75rem',
                                           fontWeight: 600, cursor: 'pointer' }}>
                            Mark as Lead
                          </button>
                          <button type="button"
                                  onClick={(e) => { e.stopPropagation(); dismissRealtimeAlert(alert._id); }}
                                  data-testid={`bizmail-dismiss-${alert._id}`}
                                  style={{ background: '#e2e8f0', color: '#0f172a',
                                           border: 'none', borderRadius: 6,
                                           padding: '0.32rem 0.7rem', fontSize: '0.75rem',
                                           fontWeight: 600, cursor: 'pointer' }}>
                            Dismiss
                          </button>
                        </div>
                      )}
                    </div>
                    {alert.alert_type === 'compliance_unclassified_doc' ? (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setClassifyAlert(alert); }}
                        data-testid={`classify-alert-btn-${alert._id}`}
                        style={{ alignSelf: 'center', background: '#0073EA', color: '#fff', border: 'none',
                                 borderRadius: 6, padding: '0.35rem 0.75rem', fontSize: '0.78rem',
                                 fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >
                        Classify
                      </button>
                    ) : (
                    <button
                      className="realtime-alert-dismiss"
                      onClick={(e) => { e.stopPropagation(); dismissRealtimeAlert(alert._id); }}
                      title="Dismiss this alert"
                      data-testid={`dismiss-alert-${alert._id}`}
                    >
                      &times;
                    </button>
                    )}
                  </div>
                );
              })}
            </div>
            </AlertsPanelErrorBoundary>
          )}
        </div>
      )}

      {/* IMP 11 (2026-02) — Support tickets modal */}
      <SupportTicketModal open={supportOpen} onClose={() => setSupportOpen(false)} />

      {/* 2026-02 — Business email slide-over panel + mark-as-lead flash. */}
      {bizFlash && (
        <div data-testid="bizmail-global-flash"
             style={{ position: 'fixed', top: 18, right: 18, zIndex: 9800,
                      background: '#0073EA', color: '#fff', padding: '0.7rem 1rem',
                      borderRadius: 8, boxShadow: '0 8px 20px rgba(0,115,234,0.35)',
                      fontWeight: 600, fontSize: '0.88rem' }}>
          {bizFlash}
        </div>
      )}
      <BusinessEmailSlideOver
        alert={bizEmailAlert}
        onClose={() => setBizEmailAlert(null)}
        onMarkedAsLead={(alertId, message) => {
          setRealtimeAlerts(prev => prev.filter(a => a._id !== alertId));
          setBizEmailAlert(null);
          setBizFlash(message || 'Contact moved to leads.');
          setTimeout(() => setBizFlash(''), 4000);
        }}
        onReplied={() => { /* keep alert visible — agent may dismiss manually */ }}
      />

      {classifyAlert && (
        <ClassifyDocModal
          docId={classifyAlert.reference_id}
          onClose={() => setClassifyAlert(null)}
          onResolved={() => {
            setRealtimeAlerts(prev => prev.filter(a => a._id !== classifyAlert._id));
            setClassifyAlert(null);
          }}
        />
      )}

      {/* IMP 6 (2026-02) — Follow-up wording suggestion modal */}
      {followupModal && (
        <div className="modal-overlay"
             data-testid="followup-modal-overlay"
             onClick={() => !followupModal.sending && setFollowupModal(null)}
             style={{ position: 'fixed', inset: 0,
                      background: 'rgba(15, 23, 42, 0.55)',
                      display: 'flex', alignItems: 'center',
                      justifyContent: 'center', zIndex: 9000 }}>
          <div
            onClick={e => e.stopPropagation()}
            data-testid="followup-modal"
            style={{ background: '#fff', borderRadius: 14,
                     width: 'min(640px, 92vw)', padding: '1.25rem 1.4rem',
                     boxShadow: '0 30px 80px rgba(15,23,42,0.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
                          alignItems: 'flex-start', marginBottom: '0.6rem' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a' }}>
                  Suggest follow-up wording
                </h3>
                <div style={{ color: '#64748b', fontSize: '0.82rem', marginTop: 2 }}>
                  {followupModal.tenantName || 'Tenant'}
                  {followupModal.tenantEmail ? ` · ${followupModal.tenantEmail}` : ''}
                </div>
              </div>
              <button
                type="button"
                onClick={() => !followupModal.sending && setFollowupModal(null)}
                data-testid="btn-close-followup-modal"
                style={{ background: 'transparent', border: 'none',
                         fontSize: '1.4rem', cursor: 'pointer',
                         color: '#94a3b8' }}>×</button>
            </div>
            {followupModal.loading ? (
              <div data-testid="followup-loading"
                   style={{ padding: '2rem 0', textAlign: 'center',
                            color: '#64748b' }}>
                Drafting a follow-up reply…
              </div>
            ) : (
              <>
                <label style={{ display: 'block', fontSize: '0.75rem',
                                color: '#334155', fontWeight: 600,
                                textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Subject
                </label>
                <input
                  type="text"
                  value={followupModal.subject}
                  onChange={e => setFollowupModal(prev => prev ? { ...prev, subject: e.target.value } : prev)}
                  data-testid="input-followup-subject"
                  style={{ width: '100%', padding: '0.5rem 0.65rem', marginTop: 4,
                           border: '1px solid #cbd5e1', borderRadius: 6,
                           fontSize: '0.9rem' }}
                />
                <label style={{ display: 'block', fontSize: '0.75rem',
                                color: '#334155', fontWeight: 600,
                                textTransform: 'uppercase', letterSpacing: '0.05em',
                                marginTop: 10 }}>
                  Reply
                </label>
                <textarea
                  value={followupModal.bodyText}
                  onChange={e => setFollowupModal(prev => prev ? { ...prev, bodyText: e.target.value } : prev)}
                  rows={9}
                  data-testid="input-followup-body"
                  style={{ width: '100%', padding: '0.6rem 0.7rem', marginTop: 4,
                           border: '1px solid #cbd5e1', borderRadius: 6,
                           fontSize: '0.92rem', lineHeight: 1.55,
                           fontFamily: 'inherit' }}
                />
                {followupModal.error && (
                  <div data-testid="followup-error"
                       style={{ color: '#b91c1c', fontSize: '0.85rem', marginTop: 8 }}>
                    {followupModal.error}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: 14,
                              justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => setFollowupModal(null)}
                    disabled={followupModal.sending}
                    data-testid="btn-cancel-followup"
                    style={{ background: '#e2e8f0', color: '#0f172a',
                             border: 'none', borderRadius: 6,
                             padding: '0.5rem 1rem', fontWeight: 600,
                             cursor: followupModal.sending ? 'not-allowed' : 'pointer' }}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={sendFollowupNow}
                    disabled={followupModal.sending}
                    data-testid="btn-send-followup"
                    style={{ background: followupModal.sending ? '#94a3b8' : '#0073EA',
                             color: '#fff', border: 'none', borderRadius: 6,
                             padding: '0.5rem 1.1rem', fontWeight: 600,
                             cursor: followupModal.sending ? 'not-allowed' : 'pointer' }}>
                    {followupModal.sending ? 'Sending…' : 'Send follow-up'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      
      {/* Alert Bar */}
      <div className="alert-bar">
        <div className="alert-badges">
          <div 
            className="alert-badge tile-red"
            onClick={() => scrollToSection('pending-messages')}
            data-testid="badge-pending"
          >
            <span className="badge-count">{pendingCount}</span>
            <span className="badge-label">Pending Approvals</span>
          </div>
          
          <div 
            className="alert-badge tile-blue"
            onClick={() => scrollToSection('active-cases')}
            data-testid="badge-cases"
          >
            <span className="badge-count">{activeCasesCount}</span>
            <span className="badge-label">Active S8 Cases</span>
          </div>
          
          <div 
            className="alert-badge tile-amber"
            onClick={() => scrollToSection('compliance-status')}
            data-testid="badge-compliance"
          >
            <span className="badge-count">{complianceAlertsCount}</span>
            <span className="badge-label">Compliance Alerts</span>
          </div>
          
          <div 
            className="alert-badge tile-green"
            onClick={() => scrollToSection('arrears-overview')}
            data-testid="badge-arrears"
          >
            <span className="badge-count">{arrearsCount}</span>
            <span className="badge-label">Arrears</span>
          </div>
          
          <div 
            className="alert-badge tile-purple"
            onClick={() => scrollToSection('whatsapp-alerts')}
            data-testid="badge-whatsapp-alerts"
            style={{ display: WHATSAPP_ENABLED ? undefined : 'none' }}
          >
            <span className="badge-count">{whatsappAlertsCount}</span>
            <span className="badge-label">Unknown WhatsApp</span>
          </div>
          
          <div 
            className="alert-badge tile-orange"
            onClick={() => scrollToSection('duplicate-alerts')}
            data-testid="badge-duplicate-alerts"
          >
            <span className="badge-count">{duplicateAlertsCount}</span>
            <span className="badge-label">Duplicate Contacts</span>
          </div>

          {/* CHANGE 1 — Available Properties tile removed. Use the
              sidebar Properties → Available item instead. */}

          <div
            className="alert-badge tile-grey"
            onClick={() => scrollToSection('maintenance-board')}
            data-testid="badge-maintenance"
          >
            <span className="badge-count">{maintenanceCounts.open_count || 0}</span>
            <span className="badge-label">Open Maintenance</span>
          </div>

          {/* 2026-06-04 — Contractors tile. Orange to match the new
              Contractor Management module. Click → /contractors page. */}
          <div
            className="alert-badge"
            onClick={() => setCurrentView('contractors')}
            data-testid="badge-contractors"
            style={{ background: '#fff7ed', color: '#9a3412',
                     border: '1px solid #fed7aa', cursor: 'pointer' }}
          >
            <span className="badge-count" style={{ color: '#ea580c' }}>
              {contractorCount}
            </span>
            <span className="badge-label">Contractors</span>
          </div>

          <div
            className="alert-badge tile-teal"
            onClick={() => scrollToSection('leads-section')}
            data-testid="badge-leads"
          >
            <span className="badge-count">{leadCounts.new || 0}</span>
            <span className="badge-label">New Leads</span>
          </div>
        </div>
      </div>
      
      <div className="dashboard-container">
        {/* Section 8 Alerts — urgent threshold/14-day alerts */}
        {s8Alerts.length > 0 && (
          <section id="s8-alerts" className="dashboard-section section-s8-alerts" data-testid="s8-alerts-section">
            <div className="section-header">
              <h2 className="section-title s8-alert-title">Section 8 Alerts</h2>
            </div>
            {s8Alerts.map(alert => (
              <div key={alert._id} className="s8-alert-banner" data-testid={`s8-alert-${alert._id}`}>
                <div className="s8-alert-content">
                  <strong>{alert.title}</strong>
                  <p>{alert.message}</p>
                </div>
                <div className="s8-alert-actions">
                  <Link to={`/section8/${alert.tenancy_id}`} className="btn-primary btn-sm" style={{ background: '#0073EA', color: '#fff' }} data-testid={`s8-consider-${alert._id}`}>
                    Consider Section 8
                  </Link>
                  <button className="btn-secondary btn-sm" onClick={async () => {
                    await fetch(`${API_BASE_URL}/api/section8/alerts/${alert._id}/dismiss`, {
                      method: 'POST', headers: { 'Authorization': `Bearer ${API_KEY}`, 'Content-Type': 'application/json' }
                    });
                    fetchDashboardData();
                  }} data-testid={`s8-dismiss-${alert._id}`}>
                    Dismiss
                  </button>
                </div>
              </div>
            ))}
          </section>
        )}

        {/* Section 2: Pending Messages — Large Actionable Cards */}
        {pendingCount > 0 && (
          <section id="pending-messages" className="dashboard-section section-urgent">
            <div className="section-header">
              <h2 className="section-title">Pending Message Approvals</h2>
              <div style={{display:'flex',alignItems:'center',gap:'1rem'}}>
                <p className="section-subtitle">These messages require your immediate attention</p>
                <Link to="/messages/pending" className="btn-primary" data-testid="link-view-all-pending" style={{whiteSpace:'nowrap', fontSize:'0.85rem', padding:'0.5rem 1rem', background:'#0073EA', color:'#fff', borderRadius:6, fontWeight:600, textDecoration:'none'}}>View All Pending Messages</Link>
              </div>
            </div>
            
            <div className="pending-grid">
              {visiblePendingMessages.map((msg) => (
                <div key={msg._id} className="pending-card" data-testid={`pending-card-${msg._id}`}>
                  <div className="pending-card-header">
                    <div>
                      <Link to={`/tenants/${msg.tenant_id}`} className="pending-card-tenant">{msg.tenant_name}</Link>
                      <div className="pending-card-property">{msg.property_address}</div>
                    </div>
                    <span className={`pending-card-channel ${msg.channel === 'whatsapp' ? 'channel-whatsapp' : 'channel-email'}`}>
                      {msg.channel}
                    </span>
                  </div>
                  <div className="pending-card-flag">{humaniseFlagReason(msg.flag_reason)}</div>
                  <div className="pending-card-preview">
                    {editingMessage === msg._id ? (
                      <textarea value={editedContent} onChange={e => setEditedContent(e.target.value)} style={{width:'100%',padding:'0.375rem',borderRadius:'6px',border:'1px solid var(--border)',fontSize:'0.75rem',resize:'vertical'}} rows="3" />
                    ) : (
                      msg.drafted_content_text
                        ? decodeText(msg.drafted_content_text).substring(0, 120) + (decodeText(msg.drafted_content_text).length > 120 ? '...' : '')
                        : ''
                    )}
                  </div>
                  <div className="pending-card-actions">
                    {editingMessage === msg._id ? (
                      <>
                        <button onClick={() => handleApprove(msg, true)} className="btn-approve" data-testid="btn-submit-edit">Submit</button>
                        <button onClick={() => { setEditingMessage(null); setEditedContent(''); }} className="btn-secondary btn-sm">Cancel</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => handleApprove(msg, false)} className="btn-approve" data-testid="btn-approve">Approve</button>
                        <button onClick={() => { setEditingMessage(msg._id); setEditedContent(decodeText(msg.drafted_content_text)); }} className="btn-secondary btn-sm" data-testid="btn-edit">Edit</button>
                        <button onClick={() => handleReject(msg)} className="btn-reject" data-testid="btn-reject">Reject</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <DashboardSeeMore to="/messages/pending" totalRemaining={pendingPager.remaining} totalCount={pendingMessages.length} testid="pending-messages" />
          </section>
        )}
        
        {/* Section 2b: WhatsApp Alerts - hidden when WHATSAPP_ENABLED=false */}
        {WHATSAPP_ENABLED && (
        <section id="whatsapp-alerts" className="dashboard-section section-whatsapp-alerts">
          <div className="section-header">
            <h2 className="section-title">Unknown WhatsApp Contacts</h2>
            {whatsappAlertsCount > 0 && (
              <p className="section-subtitle">These contacts are not in your system. Click a contact to take action.</p>
            )}
          </div>
          {whatsappAlertsCount > 0 ? (
            <div className="wa-alert-cards">
              {whatsappAlerts.map((alert) => (
                <div key={alert._id} className="wa-alert-card" data-testid={`wa-alert-${alert._id}`}>
                  <div className="wa-card-top" onClick={() => setExpandedWaAlert(expandedWaAlert === alert._id ? null : alert._id)}>
                    <div className="wa-card-info">
                      <span className="wa-phone">{alert.sender_phone}</span>
                      <span className="wa-preview">{alert.message_content.substring(0, 80)}...</span>
                    </div>
                    <div className="wa-card-meta">
                      <span className="wa-time">{formatDate(alert.received_at)}</span>
                      <span className="wa-expand-arrow">{expandedWaAlert === alert._id ? '\u25B2' : '\u25BC'}</span>
                    </div>
                  </div>
                  {expandedWaAlert === alert._id && (
                    <div className="wa-card-actions" data-testid={`wa-actions-${alert._id}`}>
                      <div className="wa-contact-detail">
                        <p><strong>Phone:</strong> {alert.sender_phone}</p>
                        <p><strong>Message:</strong> {alert.message_content}</p>
                      </div>
                      <div className="wa-action-buttons">
                        <button className="btn-primary" onClick={() => {
                          const name = prompt('Enter tenant name to search:');
                          if (!name) return;
                          fetch(`${API_BASE_URL}/api/tenants/search?q=${encodeURIComponent(name)}`, { headers: { 'Authorization': `Bearer ${API_KEY}` } })
                            .then(r => r.text()).then(t => {
                              const results = t ? JSON.parse(t) : [];
                              if (results.length === 0) { window.alert('No tenants found matching that name.'); return; }
                              const list = results.map((r, i) => `${i+1}. ${r.first_name} ${r.last_name} (${r.phone_number || r.email || 'no contact'})`).join('\n');
                              const choice = prompt(`Select tenant number:\n${list}`);
                              if (choice && results[parseInt(choice)-1]) {
                                handleLinkToTenant(alert._id, results[parseInt(choice)-1].id);
                              }
                            }).catch(() => window.alert('Search failed'));
                        }} data-testid="btn-link-tenant">
                          Link to Existing Tenant
                        </button>
                        <button className="btn-approve" onClick={() => {
                          const firstName = prompt('First name:');
                          if (!firstName) return;
                          const lastName = prompt('Last name:');
                          if (!lastName) return;
                          handleCreateTenant(alert._id, firstName, lastName, '');
                        }} data-testid="btn-create-record">
                          Create New Record
                        </button>
                        <button className="btn-reject" onClick={() => handleDismissWhatsAppAlert(alert._id)} data-testid="btn-dismiss-wa">Dismiss</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <EmptyStatePanel
              testid="whatsapp-empty-state"
              title="No unknown contacts."
              description="When someone messages your connected WhatsApp number and their phone number is not recognised, they will appear here. Estatel AI will attempt to identify them through a triage conversation. You can then link them to an existing tenant or lead record."
            />
          )}
        </section>
        )}
        
        {/* Section 2c: Duplicate Contact Alerts */}
        <section id="duplicate-alerts" className="dashboard-section section-duplicate-alerts" data-testid="duplicate-alerts-section">
          <div className="section-header">
            <h2 className="section-title">Possible Duplicate Contacts</h2>
            {duplicateAlertsCount > 0 && (
              <p className="section-subtitle">{duplicateAlertsCount} potential duplicate records detected. Click a record to review and resolve.</p>
            )}
          </div>
          {duplicateAlertsCount > 0 ? (
            <div className="dup-alert-cards">
              {duplicateAlerts.map((dup) => (
                <div key={dup._id} className="dup-alert-card" data-testid={`duplicate-alert-${dup._id}`}>
                  <div className="dup-card-top" onClick={() => setExpandedDupAlert(expandedDupAlert === dup._id ? null : dup._id)}>
                    <div className="dup-card-info">
                      <span className="dup-names">{dup.tenant_1_name} <span className="dup-vs">vs</span> {dup.tenant_2_name}</span>
                      <span className="dup-conflict">
                        {dup.conflicting_phone && `Phone: ${dup.conflicting_phone}`}
                        {dup.conflicting_phone && dup.conflicting_email && ' | '}
                        {dup.conflicting_email && `Email: ${dup.conflicting_email}`}
                        {' — '}{dup.conflict_type?.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <div className="dup-card-meta">
                      <span className="dup-date">Detected: {formatDate(dup.detected_at)}</span>
                      <span className="dup-expand-arrow">{expandedDupAlert === dup._id ? '\u25B2' : '\u25BC'}</span>
                    </div>
                  </div>
                  {expandedDupAlert === dup._id && (
                    <div className="dup-card-actions" data-testid={`dup-actions-${dup._id}`}>
                      <div className="dup-action-detail">
                        <p><strong>Tenant A:</strong> {dup.tenant_1_name} (ID: {dup.tenant_1_id})</p>
                        <p><strong>Tenant B:</strong> {dup.tenant_2_name} (ID: {dup.tenant_2_id})</p>
                      </div>
                      <div className="dup-action-buttons">
                        <button className="btn-approve" onClick={() => openResolveView(dup)} data-testid={`btn-resolve-${dup._id}`}>
                          Resolve Duplicate
                        </button>
                        <button className="btn-reject" onClick={() => {
                          if (window.confirm('Dismiss this duplicate alert? It will not appear again.')) {
                            handleDismissDuplicate(dup._id);
                          }
                        }} data-testid={`btn-dismiss-dup-${dup._id}`}>
                          Not a Duplicate — Dismiss
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <EmptyStatePanel
              testid="duplicates-empty-state"
              title="No duplicate contacts detected."
              description="Estatel AI automatically scans for contacts whose phone number or email address appears on more than one record. If a duplicate is found it will appear here for you to review and resolve manually."
            />
          )}
        </section>
        
        {/* Section 3: Active Section 8 Cases */}
        <section id="active-cases" className="dashboard-section">
          <div className="section-header">
            <h2 className="section-title">Active Section 8 Cases</h2>
            <p className="section-subtitle">{activeCasesCount} cases in progress</p>
          </div>
          
          {activeCasesCount > 0 ? (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Tenant Name</th>
                    <th>Property</th>
                    <th>Stage</th>
                    <th>Arrears</th>
                    <th>Next Deadline</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleActiveCases.map((caseItem) => {
                    const deadlineClass = getDeadlineClass(caseItem.next_deadline);
                    return (
                      <tr key={caseItem.id} className={`${deadlineClass} row-clickable`} onClick={() => window.location.href = `/section8/case/${caseItem.id}`} style={{cursor:'pointer'}}>
                        <td className="cell-bold">{caseItem.tenant_name}</td>
                        <td>{caseItem.property_address || 'N/A'}</td>
                        <td>
                          <span className="stage-badge">
                            {caseItem.stage} — {STAGE_LABELS[caseItem.stage]}
                          </span>
                        </td>
                        <td>{caseItem.total_arrears ? formatCurrency(caseItem.total_arrears) : formatCurrency(0)}</td>
                        <td>{formatDate(caseItem.next_deadline)}</td>
                        <td>
                          <Link to={`/section8/case/${caseItem.id}`} className="btn-primary btn-sm" style={{ background: '#0073EA', color: '#fff' }} data-testid={`s8-view-case-${caseItem.id}`}>View Case</Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <DashboardSeeMore to="/section8-cases-all" totalRemaining={activeCasesPager.remaining} totalCount={activeCases.length} testid="active-cases" />
            </div>
          ) : (
            <EmptyStatePanel
              testid="active-cases-empty-state"
              title="No active Section 8 cases."
              description="When a tenant reaches 2 months arrears you will be alerted to consider starting a Section 8 possession case. You can only start a case manually — Estatel AI never initiates proceedings automatically. If you have a case already in progress before joining Estatel AI use the Import Existing Case button below."
              actions={[
                { label: 'Import Existing Case', testid: 'btn-import-existing-case', onClick: () => { setCurrentView('tenants'); fetchAllTenants(); } },
              ]}
            />
          )}
        </section>

        {/* Section 4: Compliance Status */}
        <section id="compliance-status" className="dashboard-section">
          <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div>
              <h2 className="section-title">Compliance Status</h2>
              <p className="section-subtitle">
                Every tracked certificate and licence across your portfolio
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                className="btn-primary"
                style={{ background: '#0073EA' }}
                onClick={() => { setShowComplianceUpload(!showComplianceUpload); if (!showComplianceUpload) setShowBulkUpload(false); }}
                data-testid="compliance-upload-toggle-btn"
              >
                {showComplianceUpload ? 'Close Upload' : 'Upload Document'}
              </button>
              <button
                className="btn-primary"
                style={{ background: '#8b5cf6' }}
                onClick={() => { setShowBulkUpload(!showBulkUpload); if (!showBulkUpload) setShowComplianceUpload(false); }}
                data-testid="bulk-upload-toggle-btn"
              >
                {showBulkUpload ? 'Close Bulk' : 'Bulk Upload'}
              </button>
            </div>
          </div>

          {showComplianceUpload && (
            <ComplianceUpload
              onClose={() => setShowComplianceUpload(false)}
              onSaved={() => { fetchDashboardData(true); setComplianceBoardKey(k => k + 1); }}
            />
          )}

          {showBulkUpload && (
            <BulkUpload
              onClose={() => setShowBulkUpload(false)}
              onSaved={() => { fetchDashboardData(true); setComplianceBoardKey(k => k + 1); }}
            />
          )}
          
          <ComplianceBoard refreshKey={complianceBoardKey} />
        </section>
        
        {/* Section 5: Arrears Overview */}
        <section id="arrears-overview" className="dashboard-section">
          <div className="section-header">
            <h2 className="section-title">Arrears Overview</h2>
            <p className="section-subtitle">
              {arrearsCount} tenancies with outstanding arrears | 
              Total: {formatCurrency(digestSummary?.arrears_summary?.total_balance)}
            </p>
          </div>
          
          {arrearsCount > 0 && digestSummary?.arrears_details ? (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Tenant Name</th>
                    <th>Property</th>
                    <th>Total Arrears</th>
                    <th>Monthly Rent</th>
                    <th>Months in Arrears</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleArrears.map((arrears, index) => {
                    const isCritical = arrears.months_in_arrears >= 2;
                    return (
                      <tr key={index} className={`row-clickable ${isCritical ? 'bg-red-100' : ''}`} style={{cursor:'pointer'}} onClick={() => window.location.href = `/tenants/${arrears.tenant_id}?tab=tenancy`} data-testid={isCritical ? 'arrears-critical-row' : 'arrears-row'}>
                        <td className="cell-bold">{arrears.tenant_name}</td>
                        <td>
                          {arrears.property_id ? (
                            <Link to={`/properties/${arrears.property_id}`}
                                  onClick={e => e.stopPropagation()}
                                  style={{ color: '#0073EA', textDecoration: 'none' }}>
                              {arrears.property_address}
                            </Link>
                          ) : arrears.property_address}
                        </td>
                        <td className={isCritical ? 'cell-critical' : ''}>{formatCurrency(arrears.total_balance)}</td>
                        <td>{formatCurrency(arrears.rent_amount)}</td>
                        <td className={isCritical ? 'cell-critical font-bold' : ''}>{arrears.months_in_arrears} months</td>
                        <td><Link to={`/tenants/${arrears.tenant_id}?tab=tenancy`} className="btn-primary btn-sm" style={{ background: '#0073EA', color: '#fff' }} onClick={e => e.stopPropagation()} data-testid={`arrears-view-tenant-${index}`}>View Tenant</Link></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <DashboardSeeMore to="/arrears-all" totalRemaining={arrearsPager.remaining} totalCount={(digestSummary?.arrears_details || []).length} testid="arrears" />
            </div>
          ) : (
            <EmptyStatePanel
              testid="arrears-empty-state"
              title="No arrears recorded."
              description="This section shows tenants with outstanding rent balances. Arrears appear here automatically when you log a missed payment against a tenancy or when a payment is overdue. To record arrears open a tenant's detail page and use the Log Payment button in the Tenancy tab."
            />
          )}
        </section>
        
        {/* Section 6: Maintenance Board */}
        <section id="maintenance-board" className="dashboard-section" data-testid="maintenance-section">
          <MaintenanceKanban
            key={kanbanRefreshKey}
            tenancies={tenancyList}
            onOpenDetail={(id) => setMaintDetailId(id)}
            highlightMrId={maintHighlightMrId}
            onHighlightConsumed={() => setMaintHighlightMrId(null)}
          />
        </section>

        {/* Maintenance Detail Modal */}
        {maintDetailId && (
          <MaintenanceDetail
            requestId={maintDetailId}
            onClose={() => {
              setMaintDetailId(null);
              // Clear the ?pane=pending param set by pending-forward
              // alert deep-links so it doesn't persist for the next
              // MR opened via a regular kanban click.
              try {
                const url = new URL(window.location.href);
                if (url.searchParams.has('pane')) {
                  url.searchParams.delete('pane');
                  window.history.replaceState(null, '', url.toString());
                }
              } catch (_e) { /* non-fatal */ }
            }}
            onUpdated={() => { setMaintDetailId(null); setKanbanRefreshKey(k => k + 1); }}
          />
        )}

        {/* 2026-02 (Round 1 remainder — Category B1 finding).
            The `ContractorReplyTriage` modal POSTs to
            `/maintenance/contractor-thread/{outreachId}/accept`
            — an endpoint DELETED at maintenance.py:1215. Every
            action inside it 404-ed silently. Superseded by the
            on-card Approve / Reject Quote buttons in
            Maintenance.js. Mount removed rather than
            rewired — the v2 controls fully replace it. Component
            file left on disk in case any external test / doc
            still references it; safe to delete in a later
            cleanup once we're sure. */}

        {/* Step 47 — Add Lead modal (manual lead creation from the Leads empty state). */}
        {showAddLeadModal && (
          <AddLeadModal
            onClose={() => setShowAddLeadModal(false)}
            onCreated={() => { setShowAddLeadModal(false); fetchDashboardData(true); }}
          />
        )}

        {/* Step 53 BUG 3 — Add Property modal. */}
        {showAddPropertyModal && (
          <AddPropertyModal
            onClose={() => setShowAddPropertyModal(false)}
            onCreated={() => { setShowAddPropertyModal(false); fetchDashboardData(true); }}
          />
        )}

        {/* CHANGE 1 — Available Properties section removed from
            dashboard. Use the sidebar Properties → Available view. */}

        {/* Leads — Estatel AI AI triage outcomes */}
        <section id="leads-section" className="dashboard-section" data-testid="leads-section">
          <div className="section-header">
            <h2 className="section-title">Leads</h2>
            <p className="section-subtitle">
              {leadCounts.total || 0} lead{(leadCounts.total || 0) === 1 ? '' : 's'} captured by Estatel AI AI triage —
              {' '}{leadCounts.new || 0} new awaiting follow-up.
            </p>
          </div>
          {leads && leads.length > 0 ? (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Contact Type</th>
                    <th>Property of Interest</th>
                    <th>Source</th>
                    <th>Status</th>
                    <th>Received</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleLeads.map((l) => {
                    const name = [l.first_name, l.last_name].filter(Boolean).join(' ') || 'Unknown Contact';
                    return (
                      <tr key={l._id} className="row-clickable" style={{cursor:'pointer'}}
                          onClick={() => window.location.href = `/leads/${l._id}`}
                          data-testid={`lead-row-${l._id}`}>
                        <td className="cell-bold">{name}</td>
                        <td><span className={`lead-badge lead-badge-${l.contact_type || 'unknown'}`}>{(l.contact_type || 'unknown').replace('_', ' ')}</span></td>
                        <td>{(l.matched_property && l.matched_property.address) || l.property_of_interest || '—'}</td>
                        <td><span className={`lead-badge lead-source-${l.source}`}>{l.source}</span></td>
                        <td><span className={`lead-badge lead-status-${l.status || 'new'}`}>{(l.status || 'new').replace('_', ' ')}</span></td>
                        <td>{formatDate(l.created_at)}</td>
                        <td>
                          <Link to={`/leads/${l._id}`}
                                className="btn-view"
                                onClick={e => e.stopPropagation()}
                                data-testid={`lead-view-${l._id}`}>
                            View
                          </Link>
                          <span onClick={(e) => e.stopPropagation()} style={{ marginLeft: 6 }}>
                            <ArchiveButton
                              kind="lead"
                              id={l._id}
                              size="xs"
                              testid={`lead-archive-${l._id}`}
                              onArchived={() => setLeads(prev => prev.filter(x => x._id !== l._id))}
                            />
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <DashboardSeeMore to="/leads-all" totalRemaining={leadsPager.remaining} totalCount={leads.length} testid="leads" />
            </div>
          ) : (
            <EmptyStatePanel
              testid="leads-empty-state"
              title="No leads yet."
              description="Leads are created automatically when an unknown contact messages via WhatsApp or email and Estatel AI identifies them as a prospective tenant, landlord enquiry, or contractor. You can also add a lead manually."
              actions={[
                { label: 'Add Lead', testid: 'btn-add-lead', onClick: () => setShowAddLeadModal(true) },
              ]}
            />
          )}
        </section>

        {/* Section 7: All Tenancies (Collapsible) */}
        <section id="all-tenancies" className="dashboard-section">
          <div 
            className="section-header section-collapsible"
            onClick={() => setTenanciesExpanded(!tenanciesExpanded)}
          >
            <h2 className="section-title">
              {tenanciesExpanded ? '▼' : '▶'} All Tenancies
            </h2>
            <p className="section-subtitle">Click to {tenanciesExpanded ? 'collapse' : 'expand'}</p>
          </div>
          
          {tenanciesExpanded && (
            <div className="table-container">
              <p className="empty-state">Tenancies list coming soon...</p>
            </div>
          )}
        </section>
      </div>
      </main>
    </div>
  );
}

// Wrap App with Router
function AuthedChatGate() {
  // Render the floating AI Command chat only when the user is signed in.
  const { user } = useAuth();
  if (!user) return null;
  return <AICommandChat />;
}

function AppWithRouter() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          {/* Step 41 — public auth pages */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          {/* Admin route is protected by ADMIN_KEY only — NOT by agent session. */}
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/concierge" element={<AdminConcierge />} />
          {/* All other routes require an agent session */}
          <Route path="/tenants/:tenant_id" element={<ProtectedRoute><TenantDetail /></ProtectedRoute>} />
          <Route path="/properties/:property_id" element={<ProtectedRoute><PropertyDetail /></ProtectedRoute>} />
          <Route path="/messages/pending" element={<ProtectedRoute><PendingMessages /></ProtectedRoute>} />
          <Route path="/section8/:tenancy_id" element={<ProtectedRoute><S8Guard><Section8 /></S8Guard></ProtectedRoute>} />
          <Route path="/section8/case/:case_id" element={<ProtectedRoute><S8Guard><Section8Case /></S8Guard></ProtectedRoute>} />
          <Route path="/leads/:lead_id" element={<ProtectedRoute><LeadDetail /></ProtectedRoute>} />
          {/* 2026-02 — Dashboard "See more" → dedicated full-list pages */}
          <Route path="/leads-all" element={<ProtectedRoute><LeadsFullPage /></ProtectedRoute>} />
          <Route path="/arrears-all" element={<ProtectedRoute><ArrearsFullPage /></ProtectedRoute>} />
          <Route path="/section8-cases-all" element={<ProtectedRoute><ActiveCasesFullPage /></ProtectedRoute>} />
          <Route path="/maintenance-all" element={<ProtectedRoute><MaintenanceFullPage /></ProtectedRoute>} />
          <Route path="/*" element={<ProtectedRoute><App /></ProtectedRoute>} />
        </Routes>
        <AuthedChatGate />
      </AuthProvider>
    </Router>
  );
}

function S8Guard({ children }) {
  const [locked, setLocked] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const check = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stripe/billing`, {
          /* Step 41 — cookie-auth */
        });
        if (res.ok) {
          const text = await res.text();
          const d = text ? JSON.parse(text) : {};
          setLocked(d.s8_locked || d.is_read_only);
        } else {
          setLocked(false);
        }
      } catch { setLocked(false); }
    };
    check();
  }, []);

  if (locked === null) return null;
  if (locked) return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', minHeight:'60vh' }}>
      <div style={{ background:'#fff', borderRadius:'12px', padding:'2rem', maxWidth:'440px', textAlign:'center', boxShadow:'0 20px 60px rgba(0,0,0,0.12)' }}>
        <h3 style={{ margin:'0 0 0.75rem' }}>Upgrade Required</h3>
        <p style={{ fontSize:'0.875rem', color:'#374151' }}>Section 8 is included in Agency Pro at <strong>£849/mo</strong>. Upgrade to unlock.</p>
        <div style={{ display:'flex', gap:'0.75rem', justifyContent:'center', marginTop:'1.25rem' }}>
          <button className="btn-secondary" onClick={() => navigate('/')}>Back to Dashboard</button>
          <button className="btn-primary" style={{ background: '#0073EA', color: '#fff' }} onClick={() => navigate('/settings')} data-testid="btn-s8-upgrade">Upgrade Now</button>
        </div>
      </div>
    </div>
  );
  return children;
}

// Step 45 — Manual Add Tenant modal. Minimal form, no validation beyond
// empty-field guards — backend normalises/validates.
function AddTenantModal({ onClose, onCreated }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!firstName.trim() || !lastName.trim()) {
      setError('First and last name are required.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tenants/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim() || null,
          phone_number: phone.trim() || null,
        }),
      });
      const txt = await res.text();
      const data = txt ? JSON.parse(txt) : {};
      if (!res.ok) {
        setError(typeof data.detail === 'string' ? data.detail : 'Failed to create tenant.');
        return;
      }
      onCreated && onCreated(data.tenant_id);
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="add-tenant-modal"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
      }}
    >
      <div style={{ background: '#fff', borderRadius: 8, padding: '1.75rem', width: '460px', maxWidth: '92vw' }}>
        <h3 style={{ marginTop: 0 }}>Add Tenant</h3>
        <form onSubmit={submit}>
          <div className="form-group">
            <label>First Name *</label>
            <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} data-testid="add-tenant-first-name" />
          </div>
          <div className="form-group">
            <label>Last Name *</label>
            <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} data-testid="add-tenant-last-name" />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="add-tenant-email" />
          </div>
          <div className="form-group">
            <label>Phone Number</label>
            <input type="text" value={phone} onChange={e => setPhone(e.target.value)} placeholder="07911 123 456 or +447911123456" data-testid="add-tenant-phone" />
          </div>
          {error && (
            <div role="alert" style={{ background: '#fef2f2', color: '#991b1b', padding: '0.6rem 0.8rem', borderRadius: 6, fontSize: '0.88rem', marginTop: '0.5rem' }}>
              {error}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', marginTop: '1.25rem' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{ background: '#e2e8f0', color: '#1e293b', border: 'none', padding: '0.55rem 1.1rem', borderRadius: 4, cursor: 'pointer', fontWeight: 500 }}
              data-testid="add-tenant-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{ background: submitting ? '#94a3b8' : '#0073EA', color: '#fff', border: 'none', padding: '0.55rem 1.1rem', borderRadius: 4, cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: 500 }}
              data-testid="add-tenant-save"
            >
              {submitting ? 'Saving…' : 'Add Tenant'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Step 47 — Manual Add Lead modal. POSTs to POST /api/leads.
function AddLeadModal({ onClose, onCreated }) {
  const [contactType, setContactType] = useState('prospective_tenant');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [propertyOfInterest, setPropertyOfInterest] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        contact_type: contactType,
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
        property_of_interest: propertyOfInterest.trim() || null,
        notes: notes.trim() || null,
      };
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const txt = await res.text();
      const data = txt ? JSON.parse(txt) : {};
      if (!res.ok) {
        setError(typeof data.detail === 'string' ? data.detail : 'Failed to create lead.');
        return;
      }
      onCreated && onCreated(data._id);
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="add-lead-modal"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
      }}
    >
      <div style={{ background: '#fff', borderRadius: 8, padding: '1.75rem', width: '480px', maxWidth: '92vw' }}>
        <h3 style={{ marginTop: 0 }}>Add Lead</h3>
        <form onSubmit={submit}>
          <div className="form-group">
            <label>Contact Type *</label>
            <select
              value={contactType}
              onChange={e => setContactType(e.target.value)}
              data-testid="add-lead-contact-type"
            >
              <option value="prospective_tenant">Prospective Tenant</option>
              <option value="landlord_enquiry">Landlord Enquiry</option>
              <option value="contractor">Contractor</option>
              <option value="unknown">Other</option>
            </select>
          </div>
          <div className="form-group">
            <label>First Name</label>
            <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} data-testid="add-lead-first-name" />
          </div>
          <div className="form-group">
            <label>Last Name</label>
            <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} data-testid="add-lead-last-name" />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="add-lead-email" />
          </div>
          <div className="form-group">
            <label>Phone Number</label>
            <input type="text" value={phone} onChange={e => setPhone(e.target.value)} placeholder="07911 123 456 or +447911123456" data-testid="add-lead-phone" />
          </div>
          <div className="form-group">
            <label>Property of Interest</label>
            <input type="text" value={propertyOfInterest} onChange={e => setPropertyOfInterest(e.target.value)} placeholder="123 Test Street, London" data-testid="add-lead-property" />
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              data-testid="add-lead-notes"
              style={{ width: '100%', padding: '0.5rem', borderRadius: 4, border: '1px solid #cbd5e1', fontFamily: 'inherit', fontSize: '0.9rem', resize: 'vertical' }}
            />
          </div>
          {error && (
            <div role="alert" style={{ background: '#fef2f2', color: '#991b1b', padding: '0.6rem 0.8rem', borderRadius: 6, fontSize: '0.88rem', marginTop: '0.5rem' }} data-testid="add-lead-error">
              {error}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', marginTop: '1.25rem' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{ background: '#e2e8f0', color: '#1e293b', border: 'none', padding: '0.55rem 1.1rem', borderRadius: 4, cursor: 'pointer', fontWeight: 500 }}
              data-testid="add-lead-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{ background: submitting ? '#94a3b8' : '#0073EA', color: '#fff', border: 'none', padding: '0.55rem 1.1rem', borderRadius: 4, cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: 500 }}
              data-testid="add-lead-save"
            >
              {submitting ? 'Saving…' : 'Add Lead'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Step 53 BUG 3 — Manual Add Property modal. POSTs to /api/properties.
function AddPropertyModal({ onClose, onCreated }) {
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [postcode, setPostcode] = useState('');
  const [propertyType, setPropertyType] = useState('flat');
  const [bedrooms, setBedrooms] = useState('1');
  const [landlordId, setLandlordId] = useState('');
  const [landlords, setLandlords] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/landlords`, { credentials: 'include' });
        const txt = await res.text();
        const data = txt ? JSON.parse(txt) : [];
        setLandlords(Array.isArray(data) ? data : []);
      } catch (e) { /* keep empty */ }
    })();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (!addressLine1.trim() || !city.trim() || !postcode.trim()) {
      return setError('Address line 1, city and postcode are required.');
    }
    setSubmitting(true); setError(null);
    try {
      const payload = {
        address_line1: addressLine1.trim(),
        address_line2: addressLine2.trim(),
        city: city.trim(),
        postcode: postcode.trim().toUpperCase(),
        property_type: propertyType,
        bedrooms: parseInt(bedrooms, 10) || null,
        landlord_id: landlordId || null,
      };
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/properties`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const txt = await res.text();
      const data = txt ? JSON.parse(txt) : {};
      if (!res.ok) {
        setError(typeof data.detail === 'string' ? data.detail : 'Failed to create property');
        return;
      }
      onCreated && onCreated(data._id);
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div
      data-testid="add-property-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div style={{ background: '#fff', borderRadius: 8, padding: '1.75rem', width: '520px', maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto', position: 'relative' }}>
        <button
          type="button" aria-label="Close" data-testid="ap-close-x"
          onClick={onClose} disabled={submitting}
          style={{ position: 'absolute', top: '0.6rem', right: '0.6rem', background: 'transparent', border: 'none', fontSize: '1.4rem', color: '#64748b', cursor: 'pointer', lineHeight: 1, padding: '0.25rem 0.5rem' }}>
          ×
        </button>
        <h3 style={{ marginTop: 0 }}>Add Property</h3>
        <form onSubmit={submit}>
          <div className="form-group">
            <label>Address Line 1 *</label>
            <input type="text" value={addressLine1} onChange={e => setAddressLine1(e.target.value)} data-testid="ap-addr1" required />
          </div>
          <div className="form-group">
            <label>Address Line 2</label>
            <input type="text" value={addressLine2} onChange={e => setAddressLine2(e.target.value)} data-testid="ap-addr2" />
          </div>
          <div className="form-group">
            <label>City *</label>
            <input type="text" value={city} onChange={e => setCity(e.target.value)} data-testid="ap-city" required />
          </div>
          <div className="form-group">
            <label>Postcode *</label>
            <input type="text" value={postcode} onChange={e => setPostcode(e.target.value)} placeholder="SW1A 1AA" data-testid="ap-postcode" required />
          </div>
          <div className="form-group">
            <label>Property Type</label>
            <select value={propertyType} onChange={e => setPropertyType(e.target.value)} data-testid="ap-type">
              <option value="flat">Flat</option>
              <option value="house">House</option>
              <option value="studio">Studio</option>
              <option value="bungalow">Bungalow</option>
              <option value="hmo">HMO</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="form-group">
            <label>Bedrooms</label>
            <input type="number" min="0" value={bedrooms} onChange={e => setBedrooms(e.target.value)} data-testid="ap-bedrooms" />
          </div>
          <div className="form-group">
            <label>Landlord</label>
            <select value={landlordId} onChange={e => setLandlordId(e.target.value)} data-testid="ap-landlord">
              <option value="">— Select landlord (optional) —</option>
              {landlords.map(l => (
                <option key={l._id || l.id} value={l._id || l.id}>{l.full_name}{l.company_name ? ` (${l.company_name})` : ''}</option>
              ))}
            </select>
          </div>
          {error && (
            <div role="alert" style={{ background: '#fef2f2', color: '#991b1b', padding: '0.6rem 0.8rem', borderRadius: 6, fontSize: '0.88rem', marginTop: '0.5rem' }} data-testid="ap-error">
              {error}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', marginTop: '1.25rem' }}>
            <button type="button" onClick={onClose} disabled={submitting}
              style={{ background: '#e2e8f0', color: '#1e293b', border: 'none', padding: '0.55rem 1.1rem', borderRadius: 4, cursor: 'pointer', fontWeight: 500 }}
              data-testid="ap-cancel">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              style={{ background: submitting ? '#94a3b8' : '#0073EA', color: '#fff', border: 'none', padding: '0.55rem 1.1rem', borderRadius: 4, cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: 500 }}
              data-testid="ap-save">
              {submitting ? 'Adding…' : 'Add Property'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AppWithRouter;
